import Phaser from 'phaser';
import { ASSET_KEYS, COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, KAMPANJE_SALG_MILESTONE, ROOM_VIDEOS, fontStr } from '@/config';
import {
  effektivStatus,
  findFloor,
  type FloorDef,
  type FloorId,
  type FloorStatus,
} from '@/data/floors';
import { gameState } from '@/state/store';
import type {
  AnmeldelseHendelse,
  FaseOvergang,
  ForfatterManusHendelse,
  KampanjeFerdigHendelse,
  KampanjeStartetHendelse,
  KursHendelse,
  LikviditetHendelse,
  LojalitetVarselHendelse,
  SalgHendelse,
} from '@/state/GameState';
import { InboxModal } from '@/ui/InboxModal';
import { ProductionModal } from '@/ui/ProductionModal';
import { ForfatterModal } from '@/ui/ForfatterModal';
import { MarkedModal } from '@/ui/MarkedModal';
import { UtenlandsModal } from '@/ui/UtenlandsModal';
import { BokhandelModal } from '@/ui/BokhandelModal';
import { ResepsjonModal } from '@/ui/ResepsjonModal';
import { SkrivelabModal } from '@/ui/SkrivelabModal';
import { DesignModal } from '@/ui/DesignModal';
import { EventCard } from '@/ui/EventCard';
import { FloorUpgradeModal } from '@/ui/FloorUpgradeModal';
import { TierCelebrationModal } from '@/ui/TierCelebrationModal';
// Runde 44: tunge modaler (AdminModal, ArsoppsummeringModal, BrageModal)
// lazy-loades via dynamic import — sparer ~50-80 KB i initial bundle
// fordi de bare trengs ved sjeldne hendelser.
import type { BrageModal as BrageModalT } from '@/ui/BrageModal';
import { SvenBrageVinnerModal } from '@/ui/SvenBrageVinnerModal';
import { PrisModal } from '@/ui/PrisModal';
import { DomDagModal, type DomDagData } from '@/ui/DomDagModal';
import { LanseringModal, type LanseringData } from '@/ui/LanseringModal';
import type { ArsoppsummeringModal as ArsoppsummeringModalT } from '@/ui/ArsoppsummeringModal';
import { finnFloorUpgrade } from '@/data/floorUpgrades';
import { TierProgressWidget } from '@/ui/TierProgressWidget';
import { DireksjonDashboard } from '@/ui/DireksjonDashboard';
import { MinKarriereModal } from '@/ui/MinKarriereModal';
import { SettingsModal } from '@/ui/SettingsModal';
import { SjangerVelgModal } from '@/ui/SjangerVelgModal';
import { PubliseringsveiModal } from '@/ui/PubliseringsveiModal';
import type { AdminModal as AdminModalT } from '@/ui/AdminModal';
import { registrerAdminShortcut } from '@/state/adminMode';
import { eventKatalog } from '@/data/eventCatalog';
import { tidligSpillKatalog } from '@/data/tidligSpillKatalog';
import { erCalidrisEventId } from '@/systems/calidris';
import { ToastManager } from '@/ui/Toast';
import { OfflineBanner } from '@/ui/OfflineBanner';
import { MorgenIntro } from '@/ui/MorgenIntro';
// Runde 44: RoomVideoLayer lazy-loades — bare relevant for etasjer med
// video-loop. Type-import for kompilator-typing.
import type { RoomVideoLayer } from '@/ui/RoomVideoLayer';
import { sfx } from '@/systems/sfx';
import { haptisk } from '@/systems/haptisk';
import { ambientAudio } from '@/systems/ambientAudio';
import { tooltipManager } from '@/ui/TooltipManager';
import { autoSpeed } from '@/systems/autoSpeed';
import { ariaMirror } from '@/ui/AriaMirror';
import { hiddenActions } from '@/ui/HiddenActions';
import { audioManager } from '@/systems/audioManager';
import { animateFabBounce } from '@/ui/modalGlide';
import { Particles } from '@/ui/Particles';
import { PrimaryButton } from '@/ui/PrimaryButton';
import { Kolonne } from '@/ui/layout';
import { HUDScene } from '@/scenes/HUDScene';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';
import { stjerneStreng } from '@/systems/anmeldelser';
import type {
  EtasjeOppgradertHendelse,
  EventValgtHendelse,
  TierOppgradertHendelse,
} from '@/state/GameState';
import type { SpillEvent } from '@/data/types';
import { TutorialController } from '@/ui/TutorialController';
import { tutorialFullfort } from '@/state/tutorial';
import { SESONG_BESKRIVELSE, SESONG_NAVN, sesongForDag, type Sesong } from '@/systems/sesong';
import { sesongOvergang } from '@/ui/SesongOvergang';
import { mikroAnim } from '@/ui/MikroAnim';
import { tilfeldigKaffeprat } from '@/systems/kaffeprat';
import {
  gameClock,
  klokkeBus,
  type TidAvansertHendelse,
} from '@/systems/gameClock';
import {
  DAG_SLUTT_MINUTT,
  TIDSKOSTNADER,
  VARSEL_GRENSE_MINUTT,
  romskifteKostnad,
} from '@/data/timeCosts';
import { andreRomPaaSammeEtasje, etasjeFor } from '@/data/building';
import {
  NPCER,
  mottFlaggFor,
  npcIRom,
  timeplanHintFor,
  type NpcDef,
} from '@/data/schedules';
import { initialerAv } from '@/data/portretter';
import { bakgrunnsMotor } from '@/systems/bakgrunnsMotor';
import { lagreMadTvNaa } from '@/systems/madtvPersist';
import { persistSaveAsync } from '@/state/persistDb';
import type { BakgrunnsKontekst } from '@/data/events';
import { rivalen } from '@/systems/rival';
import { kapelleResepsjonistKommentar } from '@/data/npcs';
import {
  KANTINE_SAMTALER_KRAV,
  KANTINE_SAMTALER_KRAV_ETTER_KONTAKT,
  RIVAL_BUD_FRIST_DAGER,
  RIVAL_LANSERING_DAGER,
  RIVAL_LANSERING_FAKTOR,
  STALL_KONTRAKT_PRIS,
} from '@/data/events';
import { bookCatalog } from '@/data/bookCatalog';
import { DagsoppsummeringModal } from '@/ui/DagsoppsummeringModal';
import { BygningIntro } from '@/ui/BygningIntro';
import { NavnInputModal } from '@/ui/NavnInputModal';
import type { BrageKategori, BrageNominasjon } from '@/systems/brage';

const PANEL_WIDTH = 360;
const PANEL_PADDING = 28;
const FAB_RADIUS = 86;
const PRODUKSJON_BOX_HEIGHT = 132;

export class ForlagshusetScene extends Phaser.Scene {
  private background?: Phaser.GameObjects.Image;
  private floorLabel?: Phaser.GameObjects.Text;
  private floorDesc?: Phaser.GameObjects.Text;
  private korridorKnapp?: PrimaryButton;
  private naboKnappContainer?: Phaser.GameObjects.Container;
  private naboKnappY = 226;
  private naboKnappSignatur = '';
  private unsubscribe?: () => void;
  private unsubscribeOverganger?: () => void;
  private unsubscribeAnmeldelser?: () => void;
  private unsubscribeSalg?: () => void;
  private unsubscribeKurs?: () => void;
  private unsubscribeEvent?: () => void;
  private unsubscribeEventValgt?: () => void;
  private unsubscribeKampanjeStartet?: () => void;
  private unsubscribeKampanjeFerdig?: () => void;
  private unsubscribeTierOppgradert?: () => void;
  private unsubscribeForfatterManus?: () => void;
  private unsubscribeSesongSkift?: () => void;
  private unsubscribeEtasjeOppgradert?: () => void;
  private unsubscribeLojalitetVarsler?: () => void;
  private unsubscribeLikviditet?: () => void;
  private unsubscribeBrageNominasjon?: () => void;
  private unsubscribeBrageHint?: () => void;
  private unsubscribeBrageVinner?: () => void;
  private unsubscribeBrageNominasjonSven?: () => void;
  private unsubscribeBrageVinnerSven?: () => void;
  private unsubscribePrisNominasjon?: () => void;
  private unsubscribePrisVinner?: () => void;
  private unsubscribeSpillerPubliseringsklar?: () => void;
  private unsubscribeArsoppsummering?: () => void;
  private inboxModal?: InboxModal;
  private productionModal?: ProductionModal;
  private forfatterModal?: ForfatterModal;
  private markedModal?: MarkedModal;
  private eventCard?: EventCard;
  private floorUpgradeModal?: FloorUpgradeModal;
  private floorUpgradeOverlay?: Phaser.GameObjects.Container;
  private tierCelebration?: TierCelebrationModal;
  // Lazy-instansierte tunge modaler (runde 44).
  private brageModal?: BrageModalT;
  private svenBrageVinnerModal?: SvenBrageVinnerModal;
  private prisModal?: PrisModal;
  private domDagModal?: DomDagModal;
  private lanseringModal?: LanseringModal;
  // Køer for de to seremoniene. Flere bøker kan utgis / få dom samme dag;
  // de vises én om gangen via pumpSeremonier() så de aldri stables oppå
  // hverandre. Lansering (utgivelse) går alltid foran dom-dag (anmeldelse).
  private domDagKo: DomDagData[] = [];
  private lanseringKo: LanseringData[] = [];
  private arsoppsummeringModal?: ArsoppsummeringModalT;
  private tierProgressWidget?: TierProgressWidget;
  private toasts?: ToastManager;
  private offlineBanner?: OfflineBanner;
  private morgenIntro?: MorgenIntro;
  private roomVideo?: RoomVideoLayer;
  private sisteDag = 0;
  private markedRomLayer?: Phaser.GameObjects.Container;
  private utenlandsModal?: UtenlandsModal;
  private bokhandelRomLayer?: Phaser.GameObjects.Container;
  private bokhandelModal?: BokhandelModal;
  private resepsjonRomLayer?: Phaser.GameObjects.Container;
  private resepsjonModal?: ResepsjonModal;
  private skrivelabModal?: SkrivelabModal;
  private designRomLayer?: Phaser.GameObjects.Container;
  private designModal?: DesignModal;
  private mittKontorRomLayer?: Phaser.GameObjects.Container;
  private kantineRomLayer?: Phaser.GameObjects.Container;
  private npcLayer?: Phaser.GameObjects.Container;
  private npcLayerSignatur = '';
  private beskjedLayer?: Phaser.GameObjects.Container;
  private kapelleRomLayer?: Phaser.GameObjects.Container;
  private stallTilbudLayer?: Phaser.GameObjects.Container;
  private direksjonDashboard?: DireksjonDashboard;
  private settingsModal?: SettingsModal;
  private minKarriereModal?: MinKarriereModal;
  private sjangerVelgModal?: SjangerVelgModal;
  private publiseringsveiModal?: PubliseringsveiModal;
  private adminModal?: AdminModalT;
  private produksjonText?: Phaser.GameObjects.Text;
  private tutorial?: TutorialController;
  private nesteDagBounds?: { x: number; y: number; w: number; h: number };
  private produksjonsBoksBounds?: { x: number; y: number; w: number; h: number };
  private sisteIProduksjonAntall = 0;
  private sisteRedigeresAntall = 0;
  private nesteDagBtn?: PrimaryButton;
  private partikler?: Particles;
  private ambientTweens: Phaser.Tweens.Tween[] = [];
  private ambientLayer?: Phaser.GameObjects.Container;
  private dagsoppsummeringModal?: DagsoppsummeringModal;
  private unsubKlokkeTid?: () => void;
  private unsubKlokkeSlutt?: () => void;

  constructor() {
    super('Forlagshuset');
  }

  create(): void {
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');
    this.partikler = new Particles(this);
    this.drawRoom();
    this.drawFloorPanel();
    this.drawCaption();
    this.drawProduksjonsBoks();
    this.drawNesteDagKnapp();
    // Alle etasjer med 1-N sub-aksjoner deler én konsolidert aksjons-FAB
    // (runde 36). Etasjer uten aksjoner skjuler FAB-en helt.
    this.drawAksjonsFab();

    this.inboxModal = new InboxModal(this);
    this.productionModal = new ProductionModal(this);
    this.forfatterModal = new ForfatterModal(this);
    this.markedModal = new MarkedModal(this);
    this.utenlandsModal = new UtenlandsModal(this);
    this.bokhandelModal = new BokhandelModal(this);
    this.resepsjonModal = new ResepsjonModal(this);
    this.skrivelabModal = new SkrivelabModal(this);
    this.designModal = new DesignModal(this);
    this.eventCard = new EventCard(this);
    this.floorUpgradeModal = new FloorUpgradeModal(this);
    this.tierCelebration = new TierCelebrationModal(this);
    // BrageModal og ArsoppsummeringModal lazy-loades — instansieres ved
    // første åpning (runde 44 bundle-optimalisering).
    this.svenBrageVinnerModal = new SvenBrageVinnerModal(this);
    this.prisModal = new PrisModal(this);
    this.domDagModal = new DomDagModal(this);
    this.lanseringModal = new LanseringModal(this);
    this.minKarriereModal = new MinKarriereModal(this);
    this.sjangerVelgModal = new SjangerVelgModal(this);
    this.publiseringsveiModal = new PubliseringsveiModal(this);
    this.minKarriereModal.setCallbacks({
      onStartManus: () => this.aapneSjangerVelg(),
      onSkrivIDag: () => this.utforSkrivIDag(),
      onVelgPubliseringsvei: () => this.aapnePubliseringsvei(),
    });
    // AdminModal lazy-loades — instansieres ved første åpning.
    this.direksjonDashboard = new DireksjonDashboard(this, {
      onAapneMinKarriere: () => this.minKarriereModal?.open(),
      onAapneAdmin: () => {
        void this.aapneAdminModal();
      },
    });
    // Settings-panel (runde 48) — åpnes fra tannhjul-knappen i HUD.
    this.settingsModal = new SettingsModal(this, {
      onAapneAdmin: () => {
        void this.aapneAdminModal();
      },
    });
    this.toasts = new ToastManager(this);
    registrerAdminShortcut();
    this.offlineBanner = new OfflineBanner(this, () => {
      this.toasts?.vis('Tilbake online', 'standard');
    });
    this.offlineBanner.mount();
    this.morgenIntro = new MorgenIntro(this);
    this.dagsoppsummeringModal = new DagsoppsummeringModal(this);
    // Startposisjonen teller ikke som KD-besøk (QA-funn ved refresh).
    this.forrigeAktivEtasje = gameState.aktivEtasje;
    // RoomVideoLayer instansieres ved første etasje som faktisk har en
    // video-mapping (runde 44 bundle-optimalisering).
    this.sisteDag = gameState.dag;

    // GameClock (fase 1): diskret varsel når dagen nærmer seg slutten,
    // og dagsoppsummering når klokka når 17:00. Fase 3: NPC-er spawner og
    // forsvinner med klokka, så hvert tidshopp re-evaluerer rommet.
    this.unsubKlokkeTid = klokkeBus.on('time:advanced', (h) => {
      this.sjekkTidsvarsel(h);
      this.refreshNpcs();
      this.tikkBakgrunnsMotor(h.minutter);
    });
    this.unsubKlokkeSlutt = klokkeBus.on('time:dayEnded', () =>
      this.visDagsoppsummering(),
    );

    this.mountTierProgress();

    this.unsubscribe = gameState.subscribe((snap) => {
      this.refreshActiveFloor(snap.aktivEtasje);
      this.refreshNaboKnapper();
      this.refreshMarkedRom();
      this.refreshMittKontorRom();
      this.refreshKantineRom();
      this.sjekkKapelleBesok(snap.aktivEtasje);
      this.refreshKapelleRom();
      this.refreshNpcs();
      this.refreshBeskjeder();
      this.refreshBokhandelRom();
      this.refreshResepsjonRom();
      this.refreshDesignRom();
      this.refreshDireksjonRom();
      this.refreshAksjonsFab();
      this.refreshBackground(snap.aktivEtasje);
      this.refreshProduksjon();
      this.refreshFloorUpgradeOverlay(snap.aktivEtasje);
      this.oppdaterNesteDagPulse();
      this.sjekkTutorialFremdrift(snap.iProduksjon);
      if (snap.dag > this.sisteDag) {
        // Ny spilldag → klokka tilbake til 08:00. Skjer her (før HUD-ens
        // egen subscriber kjører) så HUD leser fersk klokke. Bakgrunns-
        // motoren ruller dagslogg → beskjedlapper i resepsjonen.
        gameClock.nyDag();
        bakgrunnsMotor.nyDag();
        this.sjekkRivalStatusVedNyDag();
        // Lagre BÅDE hovedsaven og Mad TV-tilstanden umiddelbart etter
        // dagsrullen. Hovedsaven er ellers debouncet 150 ms — en refresh
        // i det vinduet rullet dagen tilbake mens blobben sto på ny dag
        // (QA-funn A1: duplisert dag + dupliserte beskjedlapper).
        void persistSaveAsync(gameState.snapshot);
        lagreMadTvNaa();
        // Dagen kan byttes utenom modal-knappen (a11y-knappen «Neste dag»,
        // autoSpeed) — da skal ikke gårsdagens oppsummering bli stående.
        this.dagsoppsummeringModal?.close();
        this.morgenIntro?.spill(snap.dag);
        this.visKaffepratEtterMorgen(snap.dag);
        this.sisteDag = snap.dag;
      }
    });

    this.unsubscribeOverganger = gameState.subscribeOverganger((overganger) => {
      this.visOvergangs(overganger);
    });

    this.unsubscribeAnmeldelser = gameState.subscribeAnmeldelser((hendelser) => {
      this.visAnmeldelser(hendelser);
    });

    this.unsubscribeSalg = gameState.subscribeSalg((hendelser) => {
      this.visSalgsMilestoner(hendelser);
    });

    this.unsubscribeKurs = gameState.subscribeKurs((hendelser) => {
      this.visKursFerdig(hendelser);
    });

    this.unsubscribeEvent = gameState.subscribeEvent((event) => {
      this.aapneEvent(event);
    });

    this.unsubscribeEventValgt = gameState.subscribeEventValgt((hendelse) => {
      this.visEventValgt(hendelse);
    });

    this.unsubscribeKampanjeStartet = gameState.subscribeKampanjeStartet((h) => {
      this.visKampanjeStartet(h);
    });

    this.unsubscribeKampanjeFerdig = gameState.subscribeKampanjeFerdig((hendelser) => {
      this.visKampanjeFerdig(hendelser);
    });

    this.unsubscribeTierOppgradert = gameState.subscribeTierOppgradert((h) => {
      this.visTierOppgradert(h);
    });

    this.unsubscribeForfatterManus = gameState.subscribeForfatterManus((hendelser) => {
      this.visForfatterManus(hendelser);
    });

    this.unsubscribeSesongSkift = gameState.subscribeSesongSkift((sesong) => {
      this.visSesongSkift(sesong);
    });

    this.unsubscribeEtasjeOppgradert = gameState.subscribeEtasjeOppgradert((h) => {
      this.visEtasjeOppgradert(h);
    });

    this.unsubscribeLojalitetVarsler = gameState.subscribeLojalitetVarsler((hendelser) => {
      this.visLojalitetVarsler(hendelser);
    });

    this.unsubscribeLikviditet = gameState.subscribeLikviditet((h) => {
      this.visLikviditet(h);
    });

    this.unsubscribeBrageNominasjon = gameState.subscribeBrageNominasjon((h) => {
      void this.aapneBrageModal({
        type: 'nominasjon',
        aar: h.aar,
        nominasjoner: h.nominasjoner,
      });
      const dineNominasjoner = countDineNominasjoner(h.nominasjoner);
      if (dineNominasjoner > 0) {
        this.toasts?.vis(
          `Brage-nominasjon: ${dineNominasjoner} av dine bøker er nominert`,
          'milestone',
        );
      } else {
        this.toasts?.vis('Brage-nominasjonene er ute — ingen av dine i år', 'standard');
      }
    });

    this.unsubscribeBrageHint = gameState.subscribeBrageHint((h) => {
      void this.aapneBrageModal({
        type: 'hint',
        aar: h.aar,
        toppKandidater: h.toppKandidater,
      });
    });

    this.unsubscribeBrageVinner = gameState.subscribeBrageVinner((h) => {
      autoSpeed.paus();
      void this.aapneBrageModal({
        type: 'vinner',
        aar: h.aar,
        vinnere: h.vinnere,
      });
      let dineVinnere = 0;
      for (const v of Object.values(h.vinnere)) {
        if (v && v.forlag === 'spiller') dineVinnere += 1;
      }
      if (dineVinnere > 0) {
        sfx.play('fanfare');
        // Runde 44: tung haptisk markerer den største milestonen.
        haptisk.tung();
        this.partikler?.konfetti(this, GAME_WIDTH / 2, GAME_HEIGHT * 0.35, 80);
        this.partikler?.konfetti(this, GAME_WIDTH * 0.3, GAME_HEIGHT * 0.55, 40);
        this.partikler?.konfetti(this, GAME_WIDTH * 0.7, GAME_HEIGHT * 0.55, 40);
        for (const v of Object.values(h.vinnere)) {
          if (!v || v.forlag !== 'spiller') continue;
          this.toasts?.vis(
            `Brage-vinner: «${v.tittel}»`,
            'milestone',
          );
        }
      }
    });

    this.unsubscribePrisNominasjon = gameState.subscribePrisNominasjon((h) => {
      this.prisModal?.open({
        type: 'nominasjon',
        prisId: h.prisId,
        aar: h.aar,
        nominerte: h.nominerte,
      });
      let dineNoms = 0;
      for (const liste of Object.values(h.nominerte)) {
        for (const nom of liste) if (nom.forlag === 'spiller') dineNoms += 1;
      }
      if (dineNoms > 0) {
        this.toasts?.vis(
          `${h.prisNavn}: ${dineNoms} av dine bøker er nominert`,
          'milestone',
        );
      } else {
        this.toasts?.vis(
          `${h.prisNavn}-nominasjonene er ute — ingen av dine i år`,
          'standard',
        );
      }
    });

    this.unsubscribePrisVinner = gameState.subscribePrisVinner((h) => {
      this.prisModal?.open({
        type: 'vinner',
        prisId: h.prisId,
        aar: h.aar,
        vinnere: h.vinnere,
      });
      for (const v of Object.values(h.vinnere)) {
        if (!v || v.forlag !== 'spiller') continue;
        this.toasts?.vis(
          `${h.prisNavn}-vinner: «${v.bokTittel}»`,
          'milestone',
        );
      }
    });

    this.unsubscribeBrageNominasjonSven = gameState.subscribeBrageNominasjonSven(
      (h) => {
        this.toasts?.vis(
          `${gameState.spillerFulltNavn} nominert til Brage (${h.kategori}) for «${h.tittel}»`,
          'milestone',
        );
      },
    );

    this.unsubscribeBrageVinnerSven = gameState.subscribeBrageVinnerSven((h) => {
      // Spilleren vinner Brage — den tyngste haptiske markeringen vi har.
      haptisk.tung();
      this.svenBrageVinnerModal?.open(h);
    });

    this.unsubscribeSpillerPubliseringsklar = gameState.subscribeSpillerPubliseringsklar(
      () => {
        this.toasts?.vis(
          'Du er publiseringsklar! Skriv din egen bok i neste dag.',
          'milestone',
        );
      },
    );

    this.unsubscribeArsoppsummering = gameState.subscribeArsoppsummering((o) => {
      autoSpeed.paus();
      void this.aapneArsoppsummering(o);
      this.toasts?.vis(`År ${o.aar} er oppsummert`, 'milestone');
    });

    // 5C: rivalens kolliderende lanseringer demper salget per sjanger.
    gameState.settSalgsModifikator((sjanger, dag) => rivalen.salgsFaktor(sjanger, dag));

    autoSpeed.attachScene(this);
    tooltipManager.attachScene(this);
    this.koblOppHiddenActions();

    const aktivt = gameState.aktivtEvent;
    if (aktivt) {
      const restored = this.finnAktivtEvent(aktivt.eventId);
      if (restored) this.aapneEvent(restored);
    }

    this.sisteIProduksjonAntall = gameState.iProduksjon.length;
    this.sisteRedigeresAntall = gameState.iProduksjon.filter(
      (b) => b.status !== 'kontrahert' && b.status !== 'utgitt',
    ).length;
    this.refreshNpcs();
    // Refresh mens klokka sto på 17:00: dagsoppsummeringen ville ellers
    // aldri komme tilbake (QA-funn) — vis den på nytt ved oppstart.
    if (gameClock.dagenErOver) {
      this.time.delayedCall(400, () => this.visDagsoppsummering());
    }
    this.startTutorialOmNodvendig();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      this.unsubscribeOverganger?.();
      this.unsubscribeAnmeldelser?.();
      this.unsubscribeSalg?.();
      this.unsubscribeKurs?.();
      this.unsubscribeEvent?.();
      this.unsubscribeEventValgt?.();
      this.unsubscribeKampanjeStartet?.();
      this.unsubscribeKampanjeFerdig?.();
      this.unsubscribeTierOppgradert?.();
      this.unsubscribeForfatterManus?.();
      this.unsubscribeSesongSkift?.();
      this.unsubscribeEtasjeOppgradert?.();
      this.unsubscribeLojalitetVarsler?.();
      this.unsubscribeLikviditet?.();
      this.unsubscribeBrageNominasjon?.();
      this.unsubscribeBrageHint?.();
      this.unsubscribeBrageVinner?.();
      this.unsubscribeBrageNominasjonSven?.();
      this.unsubscribeBrageVinnerSven?.();
      this.unsubscribePrisNominasjon?.();
      this.unsubscribePrisVinner?.();
      this.unsubscribeSpillerPubliseringsklar?.();
      this.unsubscribeArsoppsummering?.();
      this.unsubKlokkeTid?.();
      this.unsubKlokkeSlutt?.();
      this.dagsoppsummeringModal?.close();
      this.arsoppsummeringModal?.close();
      this.inboxModal?.close();
      this.productionModal?.close();
      this.forfatterModal?.close();
      this.markedModal?.close();
      this.bokhandelModal?.close();
      this.resepsjonModal?.close();
      this.skrivelabModal?.close();
      this.designModal?.close();
      this.eventCard?.close();
      this.floorUpgradeModal?.close();
      this.floorUpgradeOverlay?.destroy(true);
      this.tierCelebration?.close();
      this.brageModal?.close();
      this.svenBrageVinnerModal?.close();
      this.prisModal?.close();
      this.domDagModal?.close();
      this.lanseringModal?.close();
      this.domDagKo = [];
      this.lanseringKo = [];
      this.direksjonDashboard?.close();
      this.minKarriereModal?.close();
      this.sjangerVelgModal?.close();
      this.publiseringsveiModal?.close();
      this.adminModal?.close();
      this.tierProgressWidget?.destroy();
      this.toasts?.destroy();
      this.offlineBanner?.destroy();
      this.roomVideo?.skjul();
      this.tutorial?.destroy();
      for (const t of this.ambientTweens) t.stop();
      this.ambientTweens = [];
      this.ambientLayer?.destroy(true);
      this.ambientLayer = undefined;
      this.partikler?.destroy();
      this.partikler = undefined;
      this.nesteDagBtn?.destroy();
      this.nesteDagBtn = undefined;
      this.korridorKnapp?.destroy();
      this.korridorKnapp = undefined;
      this.naboKnappContainer?.destroy(true);
      this.naboKnappContainer = undefined;
      this.mittKontorRomLayer?.destroy(true);
      this.mittKontorRomLayer = undefined;
      this.kantineRomLayer?.destroy(true);
      this.kantineRomLayer = undefined;
      this.npcLayer?.destroy(true);
      this.npcLayer = undefined;
      this.beskjedLayer?.destroy(true);
      this.beskjedLayer = undefined;
      this.kapelleRomLayer?.destroy(true);
      this.kapelleRomLayer = undefined;
      this.stallTilbudLayer?.destroy(true);
      this.stallTilbudLayer = undefined;
      // Runde 44: nullstill lazy-loadede referanser så GC kan rydde
      // hele modal-grafen ved scene-bytte (forhindrer subtile lekkasjer
      // dersom Phaser-scenen gjenstartes).
      this.adminModal = undefined;
      this.brageModal = undefined;
      this.arsoppsummeringModal = undefined;
      this.roomVideo = undefined;
      gameState.settSalgsModifikator(null);
      autoSpeed.detachScene();
      tooltipManager.detachScene();
    });
  }

  private startTutorialOmNodvendig(): void {
    if (tutorialFullfort(gameState)) return;
    if (gameState.spillerForfatter.navn.length === 0) {
      const navnModal = new NavnInputModal(this);
      navnModal.open(() => {
        const bygg = new BygningIntro(this);
        bygg.spill(() => this.startTutorialFlow());
      });
      return;
    }
    const bygg = new BygningIntro(this);
    bygg.spill(() => this.startTutorialFlow());
  }

  private aapneSjangerVelg(): void {
    this.sjangerVelgModal?.open((sjanger) => {
      const ok = gameState.startManus(sjanger);
      if (ok) {
        const aktivt = gameState.spillerForfatter.aktivtManus;
        if (aktivt) {
          this.toasts?.vis(
            `Du har startet «${aktivt.tittel}». Klikk «Skriv i dag» for å progredere.`,
            'milestone',
          );
        }
      }
    });
  }

  private utforSkrivIDag(): void {
    const res = gameState.skrivIDag();
    if (!res) return;
    // Mad TV: din egen skriving spiser en time av arbeidsdagen.
    gameClock.brukTid(TIDSKOSTNADER.skriving, 'skrev på eget manus');
    if (res.ferdig) {
      this.toasts?.vis(
        'Manuset er ferdig. Velg publiseringsvei.',
        'milestone',
      );
    } else {
      this.toasts?.vis(`Du skrev i dag. ${res.progresjon.toFixed(0)}%`, 'standard');
    }
  }

  private aapnePubliseringsvei(): void {
    this.publiseringsveiModal?.open((vei) => {
      const res = gameState.velgPubliseringsvei(vei);
      this.toasts?.vis(res.melding, res.suksess ? 'milestone' : 'standard');
    });
  }

  private startTutorialFlow(): void {
    if (tutorialFullfort(gameState)) return;
    this.tutorial = new TutorialController(this);
    this.tutorial.start({
      // Innboks/forfattere er nå nested under aksjons-FAB — pek på aksjons-FAB i stedet
      innboksFab: () => this.aksjonsFabBounds(),
      innboksFørsteKort: () => this.inboxModal?.førsteTilbyRect() ?? null,
      produksjonsBoks: () => this.produksjonsBoksBounds ?? null,
      redigeringKnapp: () => this.productionModal?.førsteRedigeringKnappRect() ?? null,
      forfatterFab: () => this.aksjonsFabBounds(),
      nesteDagKnapp: () => this.nesteDagBounds ?? null,
      lukkInnboks: () => this.inboxModal?.close(),
      lukkProduksjon: () => this.productionModal?.close(),
    });

    this.events.on('tutorial-fullfort', () => {
      this.toasts?.vis('Du er i gang. Lykke til, sjefredaktør.', 'milestone');
    });
  }

  private sjekkTutorialFremdrift(iProduksjon: ReadonlyArray<{ status: string }>): void {
    const antall = iProduksjon.length;
    if (antall > this.sisteIProduksjonAntall) {
      this.tutorial?.meldHendelse('kontrakt_klikket');
    }
    this.sisteIProduksjonAntall = antall;

    const redigeres = iProduksjon.filter(
      (b) => b.status !== 'kontrahert' && b.status !== 'utgitt',
    ).length;
    if (redigeres > this.sisteRedigeresAntall) {
      this.tutorial?.advancePassivt('send-i-redigering');
    }
    this.sisteRedigeresAntall = redigeres;
  }

  private mountTierProgress(): void {
    const panelX = GAME_WIDTH - PANEL_WIDTH;
    const x = panelX + PANEL_PADDING;
    const w = PANEL_WIDTH - PANEL_PADDING * 2;
    const boxY = GAME_HEIGHT - 96 - 56 - 24 - PRODUKSJON_BOX_HEIGHT;
    const widgetY = boxY - 16 - 320;
    this.tierProgressWidget = new TierProgressWidget(this, x, widgetY, w);
    this.tierProgressWidget.mount();
  }

  private visTierOppgradert(h: TierOppgradertHendelse): void {
    // Tier-up = fanfare + cha-ching-kombo + suksess-haptisk for ekstra
    // belønnings-følelse (runde 44 lager native iOS/Android-vibrasjon
    // når kjørt i Capacitor, ellers Vibration API).
    sfx.playSekvens([
      { navn: 'fanfare', delay: 0 },
      { navn: 'cha-ching', delay: 500 },
    ]);
    haptisk.suksess();
    autoSpeed.paus();
    this.tierCelebration?.vis(h);
    this.toasts?.vis(`Tier ${h.tilTier} — ${h.tierNavn} oppnådd`, 'milestone');
    // Konfetti over hele skjerm + planlagt HUD-tier-chip-glow når modalen lukker
    this.partikler?.konfetti(this, GAME_WIDTH / 2, GAME_HEIGHT * 0.32, 60);
    this.partikler?.konfetti(this, GAME_WIDTH * 0.25, GAME_HEIGHT * 0.5, 30);
    this.partikler?.konfetti(this, GAME_WIDTH * 0.75, GAME_HEIGHT * 0.5, 30);
    this.time.delayedCall(3500, () => {
      const hud = this.scene.get('HUD') as HUDScene | undefined;
      hud?.glodTierChip?.(3000);
    });
  }

  /**
   * Lazy-loader for AdminModal (runde 44). Modulen importeres ved første
   * åpning slik at det går i en egen chunk og ikke trekker AdminModal
   * + alle dens avhengigheter inn i hovedbundle.
   */
  private async aapneAdminModal(): Promise<void> {
    if (!this.adminModal) {
      const mod = await import('@/ui/AdminModal');
      this.adminModal = new mod.AdminModal(this);
      if (this.toasts) this.adminModal.setToastManager(this.toasts);
    }
    this.adminModal.open();
  }

  /**
   * Lazy-loader for BrageModal (runde 44).
   */
  private async aapneBrageModal(
    data: import('@/ui/BrageModal').BrageModalType,
  ): Promise<void> {
    if (!this.brageModal) {
      const mod = await import('@/ui/BrageModal');
      this.brageModal = new mod.BrageModal(this);
    }
    this.brageModal.open(data);
  }

  /**
   * Lazy-loader for ArsoppsummeringModal (runde 44).
   */
  private async aapneArsoppsummering(
    o: import('@/systems/arsoppsummering').ArsOppsummering,
  ): Promise<void> {
    if (!this.arsoppsummeringModal) {
      const mod = await import('@/ui/ArsoppsummeringModal');
      this.arsoppsummeringModal = new mod.ArsoppsummeringModal(this);
    }
    this.arsoppsummeringModal.open(o);
  }

  private aapneEvent(event: SpillEvent): void {
    autoSpeed.paus();
    if (erCalidrisEventId(event.id)) {
      this.toasts?.vis('Calidris-varsel', 'kurs');
    }
    this.eventCard?.open(event);
  }

  private visEventValgt(hendelse: EventValgtHendelse): void {
    if (!this.toasts) return;
    this.toasts.vis(`«${hendelse.tittel}» → ${hendelse.oppsummering}`, 'standard');
  }

  private finnAktivtEvent(eventId: string): SpillEvent | null {
    if (eventId.startsWith('synth_')) {
      return gameState.byggSynthEvent(eventId);
    }
    const tidlig = tidligSpillKatalog.findById(eventId);
    if (tidlig) return tidlig;
    return eventKatalog.findById(eventId) ?? null;
  }

  private drawRoom(): void {
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const roomCenterX = roomWidth / 2;
    const roomCenterY = GAME_HEIGHT / 2;

    this.background = this.add
      .image(roomCenterX, roomCenterY, ASSET_KEYS.envRedaksjon)
      .setOrigin(0.5);

    const tex = this.textures.get(ASSET_KEYS.envRedaksjon).getSourceImage();
    const texW = (tex as HTMLImageElement).width || 1;
    const texH = (tex as HTMLImageElement).height || 1;
    const scale = Math.min(roomWidth / texW, GAME_HEIGHT / texH);
    this.background.setScale(scale);
  }

  private drawFloorPanel(): void {
    const panelX = GAME_WIDTH - PANEL_WIDTH;

    this.add
      .rectangle(panelX, 0, PANEL_WIDTH, GAME_HEIGHT, COLORS.white)
      .setOrigin(0, 0)
      .setAlpha(0.96);

    this.add
      .rectangle(panelX, 0, 4, GAME_HEIGHT, COLORS.wine)
      .setOrigin(0, 0)
      .setAlpha(0.85);

    // Fase 2 (Mad TV): etasjelista er erstattet av fysisk navigasjon.
    // Panelet viser «Ut i korridoren» + dørene på samme etasje; alle
    // andre etasjer nås via korridor → heis (KorridorScene/HeisScene).
    const x = panelX + PANEL_PADDING;
    const w = PANEL_WIDTH - PANEL_PADDING * 2;
    this.korridorKnapp = new PrimaryButton({
      scene: this,
      x,
      y: 124,
      width: w,
      height: 84,
      label: 'Ut i bygget',
      variant: 'secondary',
      fontSize: 24,
      pil: true,
      onClick: () => this.gaaUtIKorridoren(),
    });
    this.naboKnappY = 124 + 84 + 18;
    this.refreshNaboKnapper();
  }

  /**
   * Knapper for de andre rommene på samme fysiske etasje (fra
   * data/building.ts) — å gå dit koster 2 min. Bygges på nytt ved
   * etasjebytte.
   */
  private refreshNaboKnapper(): void {
    const naboer = andreRomPaaSammeEtasje(gameState.aktivEtasje);
    const signatur = `${gameState.aktivEtasje}:${naboer.join(',')}`;
    if (signatur === this.naboKnappSignatur) return;
    this.naboKnappSignatur = signatur;

    this.naboKnappContainer?.destroy(true);
    this.naboKnappContainer = undefined;
    if (naboer.length === 0) return;

    const panelX = GAME_WIDTH - PANEL_WIDTH;
    const x = panelX + PANEL_PADDING;
    const w = PANEL_WIDTH - PANEL_PADDING * 2;
    const container = this.add.container(0, 0);
    this.naboKnappContainer = container;

    let y = this.naboKnappY;
    const label = this.add
      .text(x, y, 'PÅ DENNE ETASJEN', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    container.add(label);
    y += 36;

    for (const rom of naboer) {
      const floor = findFloor(rom);
      if (!floor) continue;
      const h = 72;
      const bg = this.add
        .rectangle(x, y, w, h, COLORS.beige, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.oakDark, 0.4);
      const txt = this.add
        .text(
          x + 16,
          y + h / 2,
          `${floor.label}  ·  ${TIDSKOSTNADER.romskifteSammeEtasje} min`,
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(20),
            color: '#1a1a1a',
          },
        )
        .setOrigin(0, 0.5);
      container.add([bg, txt]);
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => bg.setFillStyle(COLORS.yellowWarm, 0.5));
      bg.on('pointerout', () => bg.setFillStyle(COLORS.beige, 1));
      bg.on('pointerup', () => {
        sfx.play('click');
        this.gaaTilEtasje(rom);
      });
      y += h + 12;
    }
  }

  /**
   * Spilleren går ut av rommet — rett til bygg-tverrsnittet (full
   * Mad TV, runde 52). Korridorscenen er ute av flyten.
   */
  private gaaUtIKorridoren(): void {
    sfx.play('click');
    const nr = etasjeFor(gameState.aktivEtasje)?.nr ?? 5;
    this.scene.launch('Heis', { etasjeNr: nr });
    this.scene.sleep();
  }

  private effektivStatusFor(floor: FloorDef): FloorStatus {
    return effektivStatus(floor, {
      antallUtgivelser: gameState.antallUtgivelser(),
      spillerTier: gameState.tier,
    });
  }

  /**
   * Flytter spilleren til en annen etasje OG trekker tidskostnaden
   * (fase 1: tid som valuta). Kostnaden kommer fra data/timeCosts.ts —
   * 5 min per etasje med heisen. All etasjebytting skal gå gjennom
   * denne, aldri gameState.setEtasje direkte.
   */
  private gaaTilEtasje(id: FloorId): void {
    const fra = gameState.aktivEtasje;
    if (fra === id) return;
    const kostnad = romskifteKostnad(fra, id);
    gameState.setEtasje(id);
    const til = findFloor(id);
    gameClock.brukTid(kostnad, `gå til ${til?.label ?? id}`);
  }

  /**
   * Diskret varsel idet klokka krysser «under én time igjen»-grensen.
   * Selve fargeskiftet på HUD-klokka skjer i HUDScene; toasten her er
   * engangs per kryssing.
   */
  private sjekkTidsvarsel(h: TidAvansertHendelse): void {
    const grense = DAG_SLUTT_MINUTT - VARSEL_GRENSE_MINUTT;
    if (h.fra < grense && h.til >= grense && h.til < DAG_SLUTT_MINUTT) {
      this.toasts?.vis('Under én time igjen av arbeidsdagen', 'standard');
    }
  }

  /**
   * Klokka nådde 17:00 — dagsoppsummering (placeholder i fase 1; fase 4
   * fyller den med bakgrunnshendelser). «Start ny dag» ruller spilldagen
   * videre via gameState.nesteDag(), som igjen nullstiller GameClock i
   * dag-bytte-subscriberen.
   */
  private visDagsoppsummering(): void {
    autoSpeed.paus();
    // Dagen kan ta slutt mens spilleren er i korridoren/heisen — denne
    // scenen sover da, og oppsummeringen ville åpnet usynlig (QA-funn).
    // Vekk rommet og lukk navigasjonsscenene først.
    if (this.scene.isSleeping()) {
      this.scene.stop('Korridor');
      this.scene.stop('Heis');
      this.scene.wake();
    }
    this.dagsoppsummeringModal?.open(() => {
      sfx.play('click');
      gameState.nesteDag();
    }, bakgrunnsMotor.dagensLogg());
  }

  /**
   * Bakgrunnsmotoren (fase 4): hvert tidsforbruk kan utløse en hendelse
   * et annet sted i bygget. Definisjoner og vekting i data/events.ts;
   * her bygges bare konteksten og effekten påføres.
   */
  private tikkBakgrunnsMotor(minutter: number): void {
    const ctx: BakgrunnsKontekst = {
      dag: gameState.dag,
      minutt: gameClock.naa,
      rom: gameState.aktivEtasje,
      sendtTilbakeAntall: gameState.antallSendtTilbake,
      harMottAgent: gameState.harFlag(mottFlaggFor('agent-thomas')),
      rivalVektFaktor: rivalen.rivalVektFaktor(gameState.dag),
      harAktivtRivalBud: rivalen.budet !== null,
      harBokIProduksjon: gameState.iProduksjon.length > 0,
    };
    const oppforing = bakgrunnsMotor.haandterTid(minutter, ctx);
    if (!oppforing) return;
    this.anvendBakgrunnsEffekt(oppforing.effekt);
  }

  /** Effektene er deklarert i data/events.ts — her påføres de bare. */
  private anvendBakgrunnsEffekt(effekt?: string): void {
    if (effekt === 'bonus-manus') {
      gameState.leggTilBonusManus(1);
      return;
    }
    if (effekt === 'rival-bud') {
      const kandidater = gameState.sendtTilbakeIsbn;
      if (kandidater.length === 0) return;
      const isbn = kandidater[Math.floor(Math.random() * kandidater.length)];
      const manus = bookCatalog.findByIsbn(isbn);
      if (!manus) return;
      rivalen.leggInnBud({
        isbn,
        tittel: manus.title,
        fristDag: gameState.dag + RIVAL_BUD_FRIST_DAGER,
      });
      // Agenten gir deg en siste sjanse: manuset tilbake i innboksen
      // slik at budet faktisk kan matches.
      gameState.hentTilbakeManus(isbn);
      this.toasts?.vis(
        `Kapelle Damm byr på «${manus.title}». Manuset ligger i innboksen igjen — du har ${RIVAL_BUD_FRIST_DAGER} dager.`,
        'standard',
      );
      return;
    }
    if (effekt === 'rival-lansering') {
      const maal = gameState.iProduksjon[0];
      if (!maal) return;
      rivalen.leggTilLansering({
        sjanger: maal.genre,
        tilDag: gameState.dag + RIVAL_LANSERING_DAGER,
        faktor: RIVAL_LANSERING_FAKTOR,
      });
      return;
    }
    if (effekt === 'kd-kontakt') {
      gameState.settFlag('kd_kontakt');
    }
  }

  /** Daglig rival-oppgjør (5C): bud som løper ut eller matches. */
  private sjekkRivalStatusVedNyDag(): void {
    rivalen.ryddUtlopteLanseringer(gameState.dag);
    const utfall = rivalen.sjekkBud(gameState.dag, (isbn) =>
      gameState.iProduksjon.some((b) => b.isbn === isbn) ||
      gameState.utgitteBoker.some((b) => b.isbn === isbn),
    );
    if (!utfall) return;
    if (utfall.utfall === 'tapt') {
      this.toasts?.vis(
        `«${utfall.bud.tittel}» signerte hos Kapelle Damm. Agenten beklager. Kort.`,
        'standard',
      );
    } else {
      this.toasts?.vis(
        `Du hentet inn «${utfall.bud.tittel}» foran nesa på Kapelle Damm.`,
        'milestone',
      );
    }
  }

  /**
   * Beskjedlapper i resepsjonen (fase 4): uleste lapper fra gårsdagens
   * bakgrunnshendelser. Tapp bunken for å lese og kvittere ut.
   */
  private refreshBeskjeder(): void {
    const skalVises =
      gameState.aktivEtasje === 'resepsjon' &&
      bakgrunnsMotor.ulesteBeskjeder().length > 0;
    if (!skalVises) {
      this.beskjedLayer?.destroy(true);
      this.beskjedLayer = undefined;
      return;
    }
    if (this.beskjedLayer) return;

    const lapper = bakgrunnsMotor.ulesteBeskjeder();
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const layer = this.add.container(0, 0).setDepth(7);
    this.beskjedLayer = layer;

    const bredde = 420;
    const x = roomWidth / 2 - bredde / 2;
    let y = 150;
    const tittel = this.add
      .text(x, y, `BESKJEDER (${lapper.length})`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(15),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);
    y += 34;

    for (const lapp of lapper.slice(0, 3)) {
      const tekst = this.add
        .text(x + 12, y + 12, lapp.beskjedlapp ?? lapp.oppsummering, {
          fontFamily: FONTS.body,
          fontSize: fontStr(19),
          color: '#1a1a1a',
          wordWrap: { width: bredde - 24 },
          lineSpacing: 4,
        })
        .setOrigin(0, 0);
      const h = tekst.height + 24;
      const lappBg = this.add
        .rectangle(x, y, bredde, h, 0xf9efc9, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.oakDark, 0.4)
        .setAngle(-1.2);
      layer.add([lappBg, tekst]);
      layer.bringToTop(tekst);
      y += h + 14;
    }

    const kvitter = this.add
      .text(x, y + 6, 'Tapp for å kvittere ut beskjedene', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#6b6b6b',
      })
      .setOrigin(0, 0);
    layer.add(kvitter);

    const flate = this.add
      .rectangle(x - 10, 140, bredde + 20, y - 120, 0xffffff, 0.001)
      .setOrigin(0, 0);
    flate.setInteractive({ useHandCursor: true });
    flate.on('pointerup', () => {
      sfx.play('pageturn');
      bakgrunnsMotor.markerBeskjederLest();
      this.toasts?.vis('Beskjedene er notert.', 'standard');
      this.refreshBeskjeder();
    });
    layer.add(flate);
  }

  private drawNesteDagKnapp(): void {
    const panelX = GAME_WIDTH - PANEL_WIDTH;
    const buttonWidth = PANEL_WIDTH - PANEL_PADDING * 2;
    const buttonHeight = 96;
    const x = panelX + PANEL_PADDING;
    const y = GAME_HEIGHT - buttonHeight - 56;

    this.nesteDagBounds = { x, y, w: buttonWidth, h: buttonHeight };

    this.nesteDagBtn = new PrimaryButton({
      scene: this,
      x,
      y,
      width: buttonWidth,
      height: buttonHeight,
      label: 'Neste dag',
      variant: 'primary',
      fontSize: 30,
      pil: true,
      hoverSfx: true,
      onClick: () => {
        sfx.play('click');
        gameState.nesteDag();
        this.tutorial?.meldHendelse('neste_dag_klikket');
      },
    });

    this.oppdaterNesteDagPulse();
  }

  private oppdaterNesteDagPulse(): void {
    if (!this.nesteDagBtn) return;
    const tom = gameState.innboks.length === 0 && !gameState.aktivtEvent;
    this.nesteDagBtn.pulse(tom);
  }

  private drawProduksjonsBoks(): void {
    const panelX = GAME_WIDTH - PANEL_WIDTH;
    const boxX = panelX + PANEL_PADDING;
    const boxY = GAME_HEIGHT - 96 - 56 - 24 - PRODUKSJON_BOX_HEIGHT;
    const boxW = PANEL_WIDTH - PANEL_PADDING * 2;

    this.produksjonsBoksBounds = { x: boxX, y: boxY, w: boxW, h: PRODUKSJON_BOX_HEIGHT };

    const bg = this.add
      .rectangle(boxX, boxY, boxW, PRODUKSJON_BOX_HEIGHT, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);

    this.add
      .text(boxX + 16, boxY + 14, 'I PRODUKSJON', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8B5E3C',
        fontStyle: '700',
      })
      .setOrigin(0, 0);

    this.add
      .text(boxX + boxW - 16, boxY + 14, 'Åpne ›', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#862736',
        fontStyle: '700',
      })
      .setOrigin(1, 0);

    this.produksjonText = this.add
      .text(boxX + 16, boxY + 46, '', {
        fontFamily: FONTS.body,
        fontSize: fontStr(22),
        color: '#1a1a1a',
        wordWrap: { width: boxW - 32 },
      })
      .setOrigin(0, 0);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(COLORS.yellowWarm, 0.4));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.beige, 1));
    bg.on('pointerup', () => {
      this.aapneProduksjon();
      this.tutorial?.meldHendelse('redigering_klikket');
    });

    this.refreshProduksjon();
  }

  private refreshProduksjon(): void {
    if (!this.produksjonText) return;
    const liste = gameState.iProduksjon;
    if (liste.length === 0) {
      this.produksjonText.setText('Ingen bøker i pipelinen ennå.');
      return;
    }
    const grupper: Record<string, number> = {};
    for (const b of liste) {
      grupper[b.status] = (grupper[b.status] ?? 0) + 1;
    }
    const parts = Object.entries(grupper).map(([s, n]) => `${n} ${s}`);
    this.produksjonText.setText(`${liste.length} bøker (${parts.join(', ')})`);
  }

  private aapneProduksjon(): void {
    this.productionModal?.open();
  }

  private openForfattere(): void {
    if (gameState.aktivEtasje !== 'redaksjon') return;
    this.forfatterModal?.open();
  }

  private openInnboks(): void {
    if (gameState.aktivEtasje !== 'redaksjon') return;
    this.inboxModal?.open();
  }

  private openMarked(): void {
    if (gameState.aktivEtasje !== 'marked') return;
    this.markedModal?.open();
  }

  private openUtenlands(): void {
    if (gameState.aktivEtasje !== 'marked') return;
    this.utenlandsModal?.open();
  }

  /**
   * Knytter DOM-baserte HiddenActions-knapper (runde 42) til Phaser-
   * handlerne. Disse knappene er usynlige men tabbable, og lar skjerm-
   * leser-brukere triggle hovedhandlinger uten å navigere Phaser-canvas.
   *
   * Vi setter også opp en lytter for DOM-HUD tier-chip-klikket
   * (`hud:openTier`-event) som åpner MinKarriereModal — den nærmeste
   * tier-progress-visningen vi har i runde 42.
   */
  private koblOppHiddenActions(): void {
    hiddenActions.settHandler('neste-dag', () => {
      sfx.play('click');
      gameState.nesteDag();
      this.tutorial?.meldHendelse('neste_dag_klikket');
    });
    hiddenActions.settHandler('innboks', () => {
      if (gameState.aktivEtasje !== 'redaksjon') {
        this.gaaTilEtasje('redaksjon');
      }
      this.inboxModal?.open();
    });
    hiddenActions.settHandler('forfattere', () => {
      if (gameState.aktivEtasje !== 'redaksjon') {
        this.gaaTilEtasje('redaksjon');
      }
      this.forfatterModal?.open();
    });
    hiddenActions.settHandler('direksjon', () => {
      this.gaaTilEtasje('direksjon');
    });
    hiddenActions.settHandler('min-karriere', () => {
      this.minKarriereModal?.open();
    });
    hiddenActions.settHandler('mute', () => {
      const erAv = audioManager.toggleMute();
      if (erAv) ambientAudio.slaaAv();
      else ambientAudio.slaaPaa();
    });

    // DOM-HUD tier-chip → MinKarriere som har tier-progress-visning
    const aapneTierHandler = () => this.minKarriereModal?.open();
    document.addEventListener('hud:openTier', aapneTierHandler);

    // HUD tannhjul → Settings-panel (runde 48)
    const aapneSettingsHandler = () => this.settingsModal?.open();
    document.addEventListener('hud:openSettings', aapneSettingsHandler);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      document.removeEventListener('hud:openTier', aapneTierHandler);
      document.removeEventListener('hud:openSettings', aapneSettingsHandler);
    });
  }

  private refreshMarkedRom(): void {
    const visible = gameState.aktivEtasje === 'marked';
    if (visible) {
      this.tegnMarkedRom();
    } else {
      this.markedRomLayer?.destroy(true);
      this.markedRomLayer = undefined;
    }
  }

  private refreshDireksjonRom(): void {
    const visible = gameState.aktivEtasje === 'direksjon';
    if (visible) {
      this.direksjonDashboard?.open();
    } else {
      this.direksjonDashboard?.close();
    }
  }

  private refreshBackground(id: FloorId): void {
    if (!this.background) return;
    const harVideo = !!ROOM_VIDEOS[id];
    // Lazy-instansier RoomVideoLayer ved første video-etasje (runde 44).
    if (harVideo && !this.roomVideo) {
      void import('@/ui/RoomVideoLayer').then((mod) => {
        if (!this.roomVideo) {
          this.roomVideo = new mod.RoomVideoLayer(this);
        }
        this.roomVideo.visFor(id);
      });
    } else {
      this.roomVideo?.visFor(id);
    }
    const skjulRom =
      id === 'marked' ||
      id === 'bokhandel' ||
      id === 'resepsjon' ||
      id === 'design' ||
      id === 'mittKontor' ||
      id === 'kantine' ||
      id === 'kapelleResepsjon';
    this.background.setVisible(!skjulRom && !harVideo);
    this.oppdaterAmbient(id);
    if (skjulRom || harVideo) return;
    const ny = this.textureForEtasje(id);
    if (this.background.texture.key === ny) return;
    if (!this.textures.exists(ny)) return;

    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const settNyTextur = () => {
      this.background!.setTexture(ny);
      const tex = this.textures.get(ny).getSourceImage();
      const texW = (tex as HTMLImageElement).width || 1;
      const texH = (tex as HTMLImageElement).height || 1;
      const scale = Math.min(roomWidth / texW, GAME_HEIGHT / texH);
      this.background!.setScale(scale);
    };

    if (foretrekkerRedusertBevegelse()) {
      settNyTextur();
      this.background.setAlpha(1);
      return;
    }

    this.tweens.add({
      targets: this.background,
      alpha: 0,
      duration: 200,
      ease: 'Sine.easeIn',
      onComplete: () => {
        settNyTextur();
        this.tweens.add({
          targets: this.background!,
          alpha: 1,
          duration: 200,
          ease: 'Sine.easeOut',
        });
      },
    });
  }

  private oppdaterAmbient(id: FloorId): void {
    // Rydd opp eksisterende ambient-tweens og layer
    for (const t of this.ambientTweens) t.stop();
    this.ambientTweens = [];
    this.ambientLayer?.destroy(true);
    this.ambientLayer = undefined;

    // Ambient-loop (lyd) byttes alltid — selv om reduced-motion er på.
    // Lyd er ikke bevegelse og kan være ønskelig for blinde brukere.
    ambientAudio.byttFloor(id);

    if (foretrekkerRedusertBevegelse()) return;

    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const layer = this.add.container(0, 0).setDepth(5);
    this.ambientLayer = layer;

    if (id === 'redaksjon') {
      // Kaffekopp-damp som stiger
      const damp = this.add.circle(roomWidth * 0.35, GAME_HEIGHT * 0.62, 14, 0xffffff, 0);
      layer.add(damp);
      const tween = this.tweens.add({
        targets: damp,
        y: { from: GAME_HEIGHT * 0.62, to: GAME_HEIGHT * 0.45 },
        alpha: { from: 0.55, to: 0 },
        scale: { from: 0.8, to: 1.6 },
        duration: 2400,
        ease: 'Sine.easeOut',
        repeat: -1,
      });
      this.ambientTweens.push(tween);
    } else if (id === 'marked') {
      // Podcast on air-prikk
      const onAirBg = this.add
        .rectangle(roomWidth * 0.78, GAME_HEIGHT * 0.22, 110, 36, 0x1a1a1a, 0.6)
        .setOrigin(0.5);
      const prikk = this.add.circle(roomWidth * 0.78 - 36, GAME_HEIGHT * 0.22, 8, 0xc94a4a, 1);
      const tekst = this.add
        .text(roomWidth * 0.78 + 16, GAME_HEIGHT * 0.22, 'ON AIR', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      layer.add([onAirBg, prikk, tekst]);
      const tween = this.tweens.add({
        targets: prikk,
        alpha: { from: 1, to: 0.25 },
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.ambientTweens.push(tween);
    } else if (id === 'direksjon') {
      // Boks-stabel-glow ved bordet
      const glow = this.add
        .rectangle(roomWidth * 0.55, GAME_HEIGHT * 0.7, 120, 24, COLORS.ochre, 0.3)
        .setOrigin(0.5);
      layer.add(glow);
      const tween = this.tweens.add({
        targets: glow,
        alpha: { from: 0.2, to: 0.5 },
        duration: 1800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.ambientTweens.push(tween);
    }
  }

  private textureForEtasje(id: FloorId): string {
    switch (id) {
      case 'direksjon':
        return ASSET_KEYS.envDireksjon;
      case 'kjeller':
        return ASSET_KEYS.envTrykkeri;
      case 'tak':
        return ASSET_KEYS.envTakterrasse;
      default:
        return ASSET_KEYS.envRedaksjon;
    }
  }

  private tegnMarkedRom(): void {
    if (this.markedRomLayer) return;
    this.markedRomLayer = this.byggRomMedBilde(
      ASSET_KEYS.envMarked,
      'Marked og PR',
      'Trykk på megafonen for å kjøpe kampanjer',
      true,
    );
  }

  private openBokhandel(): void {
    if (gameState.aktivEtasje !== 'bokhandel') return;
    this.bokhandelModal?.open();
  }

  private openResepsjon(): void {
    if (gameState.aktivEtasje !== 'resepsjon') return;
    this.resepsjonModal?.open();
  }

  private openDesign(): void {
    if (gameState.aktivEtasje !== 'design') return;
    this.designModal?.open();
  }

  private refreshBokhandelRom(): void {
    const visible = gameState.aktivEtasje === 'bokhandel';
    if (visible) {
      this.tegnBokhandelRom();
    } else {
      this.bokhandelRomLayer?.destroy(true);
      this.bokhandelRomLayer = undefined;
    }
  }

  private tegnBokhandelRom(): void {
    if (this.bokhandelRomLayer) return;
    this.bokhandelRomLayer = this.byggRomMedBilde(
      ASSET_KEYS.envBokhandel,
      'Bokhandel og foaje',
      'Trykk på bokstabelen for å boost backlist-bøker',
      true,
    );
  }

  private refreshResepsjonRom(): void {
    const visible = gameState.aktivEtasje === 'resepsjon';
    if (visible) {
      this.tegnResepsjonRom();
    } else {
      this.resepsjonRomLayer?.destroy(true);
      this.resepsjonRomLayer = undefined;
    }
  }

  private tegnResepsjonRom(): void {
    if (this.resepsjonRomLayer) return;
    this.resepsjonRomLayer = this.byggRomMedBilde(
      ASSET_KEYS.envResepsjon,
      'Resepsjon',
      'Trykk på kaffekoppen for å invitere forfatterne dine, eller på skrivelab-knappen',
      true,
    );
  }

  private openSkrivelab(): void {
    if (gameState.aktivEtasje !== 'resepsjon') return;
    this.skrivelabModal?.open();
  }

  private refreshMittKontorRom(): void {
    const visible = gameState.aktivEtasje === 'mittKontor';
    if (visible) {
      if (this.mittKontorRomLayer) return;
      this.mittKontorRomLayer = this.byggRomMedBilde(
        ASSET_KEYS.envMittKontor,
        'Mitt kontor',
        'Telefonen, manusbunken og utsikten. Beskjeder ender her.',
      );
    } else {
      this.mittKontorRomLayer?.destroy(true);
      this.mittKontorRomLayer = undefined;
    }
  }

  private refreshKantineRom(): void {
    const visible = gameState.aktivEtasje === 'kantine';
    if (visible) {
      if (this.kantineRomLayer) return;
      this.kantineRomLayer = this.byggRomMedBilde(
        ASSET_KEYS.envKantine,
        'Kantine',
        'Kaffe, boller og bransjeprat. Folk dukker opp her i lunsjen.',
      );
    } else {
      this.kantineRomLayer?.destroy(true);
      this.kantineRomLayer = undefined;
    }
  }

  /**
   * NPC-er i rommet (fase 3): spawnes/despawnes ut fra GameClock og
   * timeplanene i data/schedules.ts. Tomt rom viser timeplan-hint for
   * NPC-er spilleren har møtt før — før det må man finne dem selv.
   */
  private refreshKapelleRom(): void {
    const visible = gameState.aktivEtasje === 'kapelleResepsjon';
    if (visible) {
      if (this.kapelleRomLayer) return;
      this.kapelleRomLayer = this.byggRomMedBilde(
        ASSET_KEYS.envKapelleResepsjon,
        'Kapelle Damm',
        'Resepsjonen til konkurrenten. Marmoren er ekte. Smilet er det ikke.',
      );
    } else {
      this.kapelleRomLayer?.destroy(true);
      this.kapelleRomLayer = undefined;
    }
  }

  /**
   * 5A: resepsjonisten kommenterer besøket — spissere for hver gang.
   * Telles kun ved ekte OVERGANG til rommet (QA-funn: refresh med
   * lagret posisjon i rommet ga +1 besøk).
   */
  private sjekkKapelleBesok(nyEtasje: FloorId): void {
    if (nyEtasje === 'kapelleResepsjon' && this.forrigeAktivEtasje !== 'kapelleResepsjon') {
      const besokNr = rivalen.registrerBesok();
      this.toasts?.vis(kapelleResepsjonistKommentar(besokNr), 'standard');
    }
    this.forrigeAktivEtasje = nyEtasje;
  }

  private refreshNpcs(): void {
    const rom = gameState.aktivEtasje;
    // 5D: signerte stall-forfattere møter du som dine egne, ikke i kantina.
    const tilstede = npcIRom(rom, gameClock.naa, gameState.dag).filter(
      (n) => !gameState.harFlag(`stall_signert_${n.id}`),
    );
    const signatur = `${rom}|${tilstede.map((n) => n.id).join(',')}`;
    if (signatur === this.npcLayerSignatur && this.npcLayer) return;
    this.npcLayerSignatur = signatur;

    this.npcLayer?.destroy(true);
    // Direksjonen har et fulldekkende dashbord (depth 15) — der vises
    // NPC-er som kompakt besøkskort OVER panelet (fiks etter spilltest:
    // Kristine var uklikkbar bak dashbordet). Ellers vanlig portrett.
    const harDashbord = rom === 'direksjon';
    this.npcLayer = this.add.container(0, 0).setDepth(harDashbord ? 16 : 6);

    if (tilstede.length > 0) {
      tilstede.forEach((npc, i) => {
        if (harDashbord) this.tegnNpcKompakt(npc, i);
        else this.tegnNpc(npc, i);
      });
      return;
    }

    // Hint-linjer for NPC-er som pleier å være her og som er møtt.
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    let hintY = 132;
    for (const npc of NPCER) {
      if (!gameState.harFlag(mottFlaggFor(npc.id))) continue;
      const hint = timeplanHintFor(npc.id, rom);
      if (!hint) continue;
      const tekst = this.add
        .text(roomWidth / 2, hintY, hint, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(17),
          color: '#5c4630',
          backgroundColor: 'rgba(253,250,243,0.85)',
          padding: { left: 12, right: 12, top: 6, bottom: 6 },
        })
        .setOrigin(0.5, 0);
      this.npcLayer.add(tekst);
      hintY += 48;
    }
  }

  /** Kompakt besøkskort for rom med fulldekkende dashbord (direksjonen). */
  private tegnNpcKompakt(npc: NpcDef, indeks: number): void {
    if (!this.npcLayer) return;
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const w = roomWidth - 96;
    const h = 96;
    const x = 48;
    const y = 116 + indeks * (h + 14);

    const bg = this.add
      .rectangle(x, y, w, h, COLORS.beige, 0.97)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.9);
    this.npcLayer.add(bg);

    const pKey = npc.portrettKey ?? '';
    if (pKey && this.textures.exists(pKey)) {
      const img = this.add.image(x + 14, y + h / 2, pKey).setOrigin(0, 0.5);
      const tex = this.textures.get(pKey).getSourceImage();
      const th = (tex as HTMLImageElement).height || 1;
      img.setScale((h - 16) / th);
      this.npcLayer.add(img);
    } else {
      const ring = this.add.circle(x + 14 + 34, y + h / 2, 32, COLORS.white, 1);
      ring.setStrokeStyle(3, COLORS.wine, 0.9);
      const init = this.add
        .text(x + 14 + 34, y + h / 2, initialerAv(npc.navn), {
          fontFamily: FONTS.display,
          fontSize: fontStr(22),
          color: '#862736',
        })
        .setOrigin(0.5);
      this.npcLayer.add([ring, init]);
    }

    const tekst = this.add
      .text(x + 100, y + h / 2, `${npc.navn} er her`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(21),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0.5);
    const knapp = this.add
      .text(x + w - 16, y + h / 2, `Snakk (${TIDSKOSTNADER.samtaleKort} min) ›`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(17),
        color: '#862736',
        fontStyle: '700',
      })
      .setOrigin(1, 0.5);
    this.npcLayer.add([tekst, knapp]);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(COLORS.yellowWarm, 0.97));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.beige, 0.97));
    bg.on('pointerup', () => this.snakkMed(npc));
  }

  private tegnNpc(npc: NpcDef, indeks: number): void {
    if (!this.npcLayer) return;
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const x = roomWidth * (0.28 + indeks * 0.3);
    const fotY = GAME_HEIGHT - 560;
    const c = this.add.container(x, fotY);

    const teksturKey = npc.portrettKey ?? '';
    if (teksturKey && this.textures.exists(teksturKey)) {
      const img = this.add.image(0, 0, teksturKey).setOrigin(0.5, 1);
      const tex = this.textures.get(teksturKey).getSourceImage();
      const h = (tex as HTMLImageElement).height || 1;
      img.setScale(300 / h);
      c.add(img);
    } else {
      // Placeholder til portrettet er prosessert: sirkel med initialer.
      const ring = this.add.circle(0, -150, 84, COLORS.beige, 1);
      ring.setStrokeStyle(4, COLORS.wine, 0.9);
      const init = this.add
        .text(0, -150, initialerAv(npc.navn), {
          fontFamily: FONTS.display,
          fontSize: fontStr(48),
          color: '#862736',
        })
        .setOrigin(0.5);
      c.add([ring, init]);
    }

    const navnPlate = this.add
      .text(0, 16, `${npc.navn}\n${npc.tittel}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#1a1a1a',
        align: 'center',
        backgroundColor: 'rgba(253,250,243,0.9)',
        padding: { left: 10, right: 10, top: 4, bottom: 4 },
      })
      .setOrigin(0.5, 0);
    c.add(navnPlate);

    const flate = this.add
      .rectangle(0, -160, 190, 360, 0xffffff, 0.001)
      .setOrigin(0.5, 0.5);
    flate.setInteractive({ useHandCursor: true });
    flate.on('pointerup', () => this.snakkMed(npc));
    c.add(flate);

    this.npcLayer.add(c);
  }

  private sisteSamtaleTs = new Map<string, number>();
  private forrigeAktivEtasje: FloorId = 'redaksjon';

  /** Samtale med NPC — koster tid (data/timeCosts.ts) og flagger møtet. */
  private snakkMed(npc: NpcDef): void {
    // Dobbelttapp-guard (QA-funn): fire raske klikk kostet en time.
    const naa = Date.now();
    if (naa - (this.sisteSamtaleTs.get(npc.id) ?? 0) < 900) return;
    this.sisteSamtaleTs.set(npc.id, naa);
    haptisk.lett();
    sfx.play('click');
    // 5D: rekrutterbare KD-forfattere har egen relasjonsflyt i kantina.
    if (npc.forlag === 'kapelledamm' && npc.forfatterProfilId) {
      this.snakkMedStallForfatter(npc);
      return;
    }
    const forsteGang = !gameState.harFlag(mottFlaggFor(npc.id));
    if (forsteGang) {
      gameState.settFlag(mottFlaggFor(npc.id));
      this.toasts?.vis(`${npc.navn}: ${npc.hilsen}`, 'milestone');
    } else {
      this.toasts?.vis(`Dere tar en kjapp prat om bransjen.`, 'standard');
    }
    gameClock.brukTid(TIDSKOSTNADER.samtaleKort, `samtale med ${npc.navn}`);
  }

  /**
   * 5D: relasjonsbygging med KD-stallens forfatter. Tre samtaler (to
   * hvis han tok kontakt selv) før kontrakt kan tilbys. Signering
   * utløser rivalens hevn-modus og konfrontasjon i heisen.
   */
  private snakkMedStallForfatter(npc: NpcDef): void {
    if (gameState.harFlag(`stall_signert_${npc.id}`)) {
      this.toasts?.vis(`${npc.navn} er allerede din forfatter.`, 'standard');
      return;
    }
    const krav = gameState.harFlag('kd_kontakt')
      ? KANTINE_SAMTALER_KRAV_ETTER_KONTAKT
      : KANTINE_SAMTALER_KRAV;
    const n = rivalen.antallSamtaler(npc.id);
    if (n >= krav) {
      this.visStallTilbud(npc);
      return;
    }
    const nyN = rivalen.registrerSamtale(npc.id);
    gameState.settFlag(mottFlaggFor(npc.id));
    gameClock.brukTid(TIDSKOSTNADER.samtaleKort, `samtale med ${npc.navn}`);
    if (nyN === 1) {
      this.toasts?.vis(`${npc.navn}: ${npc.hilsen}`, 'milestone');
    } else if (nyN < krav) {
      this.toasts?.vis(
        `${npc.navn} åpner seg litt mer. (${nyN}/${krav} samtaler)`,
        'standard',
      );
    }
    if (nyN >= krav) {
      this.toasts?.vis(
        `${npc.navn} senker stemmen: «Hvis du hadde noe konkret å tilby … så lytter jeg.»`,
        'milestone',
      );
    }
  }

  /** Kontraktstilbudet — enkel to-knapps overlay i rommet. */
  private visStallTilbud(npc: NpcDef): void {
    if (this.stallTilbudLayer) return;
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const layer = this.add.container(0, 0).setDepth(40);
    this.stallTilbudLayer = layer;

    const backdrop = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0)
      .setInteractive();
    layer.add(backdrop);

    const w = roomWidth - 120;
    const x = 60;
    const y = GAME_HEIGHT / 2 - 280;
    const sheet = this.add
      .rectangle(x, y, w, 560, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(4, COLORS.wine, 1);
    layer.add(sheet);

    const tittel = this.add
      .text(x + 32, y + 32, `Tilby ${npc.navn} kontrakt?`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(36),
        color: '#862736',
        wordWrap: { width: w - 64 },
      })
      .setOrigin(0, 0);
    const brod = this.add
      .text(
        x + 32,
        y + 110,
        `Forskudd: ${STALL_KONTRAKT_PRIS.toLocaleString('nb-NO')} kr.\n\n` +
          'Kapelle Damm kommer til å merke det. Og Preben kommer til å si noe om det i heisen.',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(24),
          color: '#1a1a1a',
          wordWrap: { width: w - 64 },
          lineSpacing: 6,
        },
      )
      .setOrigin(0, 0);
    layer.add([tittel, brod]);

    const lukk = (): void => {
      this.stallTilbudLayer?.destroy(true);
      this.stallTilbudLayer = undefined;
    };

    const knapp = (ky: number, label: string, primaer: boolean, onClick: () => void) => {
      const bg = this.add
        .rectangle(x + 32, ky, w - 64, 88, primaer ? COLORS.wine : 0xcfc6b7, 1)
        .setOrigin(0, 0);
      const txt = this.add
        .text(x + w / 2, ky + 44, label, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(24),
          color: primaer ? '#ffffff' : '#3a2e22',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      layer.add([bg, txt]);
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerup', onClick);
    };

    knapp(y + 560 - 220, `Tilby kontrakt (${STALL_KONTRAKT_PRIS.toLocaleString('nb-NO')} kr)`, true, () => {
      if (gameState.kroner < STALL_KONTRAKT_PRIS) {
        this.toasts?.vis('Du har ikke råd til forskuddet.', 'standard');
        return;
      }
      const forfatter = gameState.rekrutterForfatter(npc.forfatterProfilId!);
      if (!forfatter) return;
      gameState.endreKroner(-STALL_KONTRAKT_PRIS);
      gameState.settFlag(`stall_signert_${npc.id}`);
      rivalen.utlosHevn(gameState.dag);
      gameClock.brukTid(TIDSKOSTNADER.samtaleLang, `kontraktsmøte med ${npc.navn}`);
      sfx.play('cha-ching');
      haptisk.suksess();
      this.toasts?.vis(
        `${forfatter.navn} signerte hos deg. Kapelle Damm kommer til å huske dette.`,
        'milestone',
      );
      lukk();
      this.refreshNpcs();
    });
    knapp(y + 560 - 112, '«Ikke ennå.»', false, () => {
      sfx.play('click');
      lukk();
    });
  }

  private refreshDesignRom(): void {
    const visible = gameState.aktivEtasje === 'design';
    if (visible) {
      this.tegnDesignRom();
    } else {
      this.designRomLayer?.destroy(true);
      this.designRomLayer = undefined;
    }
  }

  private tegnDesignRom(): void {
    if (this.designRomLayer) return;
    this.designRomLayer = this.byggRomMedBilde(
      ASSET_KEYS.envDesign,
      'Design',
      'Trykk på paletten for å sette i gang cover-eksperimenter',
      true,
    );
  }

  private aksjonsFab?: Phaser.GameObjects.Container;
  private aksjonsFanExpandert = false;
  private aksjonsFanContainere: Phaser.GameObjects.Container[] = [];
  private aksjonsBackdrop?: Phaser.GameObjects.Rectangle;
  private aksjonsFanForEtasje?: FloorId;

  private aksjonsFabBounds(): { x: number; y: number; w: number; h: number } | null {
    if (!this.aksjonsFab) return null;
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const fabX = roomWidth - FAB_RADIUS - 40;
    const fabY = GAME_HEIGHT - FAB_RADIUS - 40;
    return {
      x: fabX - FAB_RADIUS,
      y: fabY - FAB_RADIUS,
      w: FAB_RADIUS * 2,
      h: FAB_RADIUS * 2,
    };
  }

  /**
   * Tegner en konsolidert "Aksjoner"-FAB nederst-høyre. På etasjer med flere
   * sub-aksjoner ekspanderer den til en fan; på etasjer med kun én sub-aksjon
   * vises kun den underliggende FAB-en direkte (ingen aksjons-FAB).
   */
  private drawAksjonsFab(): void {
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const fabX = roomWidth - FAB_RADIUS - 40;
    const fabY = GAME_HEIGHT - FAB_RADIUS - 40;

    const container = this.add.container(fabX, fabY).setDepth(22);
    const shadow = this.add.circle(6, 8, FAB_RADIUS, 0x000000, 0.25).setOrigin(0.5);
    container.add(shadow);
    const bg = this.add
      .circle(0, 0, FAB_RADIUS, COLORS.wine, 1)
      .setOrigin(0.5)
      .setStrokeStyle(3, COLORS.white, 0.85);
    container.add(bg);
    // Runde 44: custom WebP-ikon erstatter emoji-tekst for hoved-FAB.
    // Runde 45: emoji-fallback ('⋯') fjernet. Hvis WebP-ikonet av en eller
    // annen grunn ikke er lastet (skal aldri skje siden ASSET_PATHS auto-
    // lastes i PreloadScene), tegner vi tre konsentriske prikker som
    // signaliserer "meny" — fortsatt grafiske, aldri emoji.
    let icon: Phaser.GameObjects.Image | Phaser.GameObjects.Container;
    if (this.textures.exists(ASSET_KEYS.iconFabAksjon)) {
      const img = this.add.image(0, 4, ASSET_KEYS.iconFabAksjon).setOrigin(0.5);
      const tex = this.textures.get(ASSET_KEYS.iconFabAksjon).getSourceImage();
      const w = (tex as HTMLImageElement).width || 1;
      const h = (tex as HTMLImageElement).height || 1;
      const target = 92;
      const scale = Math.min(target / w, target / h);
      img.setScale(scale);
      icon = img;
    } else {
      const dots = this.add.container(0, 4);
      dots.add(this.add.circle(-22, 0, 8, COLORS.white, 1));
      dots.add(this.add.circle(0, 0, 8, COLORS.white, 1));
      dots.add(this.add.circle(22, 0, 8, COLORS.white, 1));
      icon = dots;
    }
    container.add(icon);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => {
      bg.setFillStyle(0x6f1f2c);
      mikroAnim.wiggle(icon, { grader: 4, antall: 2, varighet: 240 });
    });
    bg.on('pointerout', () => bg.setFillStyle(COLORS.wine));
    bg.on('pointerup', () => {
      animateFabBounce(this, container);
      sfx.play('click');
      // Runde 44: lett haptisk på FAB-trykk gjør mobil-feel snappy.
      haptisk.lett();
      if (this.aksjonsFanExpandert) {
        this.kollapsAksjonsFan();
      } else {
        this.ekspandAksjonsFan();
      }
    });

    this.aksjonsFab = container;
    this.refreshAksjonsFab();
  }

  // Runde 45: emoji-feltet (`ikon`) er fjernet siden alle FAB-er nå har
  // dedikert `ikonAssetKey` med WebP-ikon. Hvis ikonet av en eller annen
  // grunn ikke er lastet, faller vi tilbake til en hvit ring (ikke emoji).
  private aksjonerForAktivEtasje(): Array<{
    ikonAssetKey: string;
    onClick: () => void;
    badge?: number;
  }> {
    const id = gameState.aktivEtasje;
    if (id === 'redaksjon') {
      return [
        {
          ikonAssetKey: ASSET_KEYS.iconFabInbox,
          onClick: () => {
            this.kollapsAksjonsFan();
            this.openInnboks();
            this.tutorial?.meldHendelse('innboks_vist');
          },
          badge: gameState.innboks.length,
        },
        {
          ikonAssetKey: ASSET_KEYS.iconFabForfattere,
          onClick: () => {
            this.kollapsAksjonsFan();
            this.openForfattere();
            this.tutorial?.meldHendelse('forfatter_vist');
          },
          badge: Object.keys(gameState.authors).length,
        },
      ];
    }
    if (id === 'marked') {
      return [
        {
          // Runde 44: marked-FAB har nå eget megafon-ikon.
          ikonAssetKey: ASSET_KEYS.iconFabMegafon,
          onClick: () => {
            this.kollapsAksjonsFan();
            this.openMarked();
          },
          badge: gameState.aktiveKampanjer.length,
        },
        {
          ikonAssetKey: ASSET_KEYS.iconFabUtenlands,
          onClick: () => {
            this.kollapsAksjonsFan();
            this.openUtenlands();
          },
          badge: gameState.utenlandsKvalifiserteBoker().length,
        },
      ];
    }
    if (id === 'resepsjon') {
      return [
        {
          ikonAssetKey: ASSET_KEYS.iconFabKaffekopp,
          onClick: () => {
            this.kollapsAksjonsFan();
            this.openResepsjon();
          },
          badge: Object.keys(gameState.authors).length,
        },
        {
          // Runde 44: skrivelab-FAB har nå eget skrivelab-ikon.
          ikonAssetKey: ASSET_KEYS.iconFabSkrivelab,
          onClick: () => {
            this.kollapsAksjonsFan();
            this.openSkrivelab();
          },
        },
      ];
    }
    if (id === 'bokhandel') {
      return [
        {
          ikonAssetKey: ASSET_KEYS.iconFabBokhandel,
          onClick: () => {
            this.kollapsAksjonsFan();
            this.openBokhandel();
          },
          badge: gameState.backlistRevivals.length,
        },
      ];
    }
    if (id === 'design') {
      return [
        {
          ikonAssetKey: ASSET_KEYS.iconFabPalett,
          onClick: () => {
            this.kollapsAksjonsFan();
            this.openDesign();
          },
          badge: gameState.iProduksjon.filter((b) => b.status === 'designes').length,
        },
      ];
    }
    return [];
  }

  private refreshAksjonsFab(): void {
    if (!this.aksjonsFab) return;
    const aksjoner = this.aksjonerForAktivEtasje();
    const skalVises = aksjoner.length >= 1;
    this.aksjonsFab.setVisible(skalVises);
    if (
      this.aksjonsFanExpandert &&
      this.aksjonsFanForEtasje !== gameState.aktivEtasje
    ) {
      this.kollapsAksjonsFan();
    }
    if (!skalVises) {
      this.kollapsAksjonsFan();
    }
  }

  private ekspandAksjonsFan(): void {
    if (!this.aksjonsFab) return;
    if (this.aksjonsFanExpandert) return;
    const aksjoner = this.aksjonerForAktivEtasje();
    if (aksjoner.length === 0) return;

    this.aksjonsFanExpandert = true;
    this.aksjonsFanForEtasje = gameState.aktivEtasje;

    // ARIA-mirror: les opp navnene på sub-FAB-ene for skjermlesere.
    // Vi mapper fra ikon-asset-key til lesbart navn for skjermleseren.
    ariaMirror.varselFabMeny(aksjoner.map((a) => fabLabelFor(a.ikonAssetKey)));

    // Backdrop som lukker fan ved klikk utenfor
    const backdrop = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.01)
      .setOrigin(0, 0)
      .setDepth(21)
      .setInteractive();
    backdrop.on('pointerup', () => this.kollapsAksjonsFan());
    this.aksjonsBackdrop = backdrop;

    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const fabX = roomWidth - FAB_RADIUS - 40;
    const fabY = GAME_HEIGHT - FAB_RADIUS - 40;
    const smaaRadius = Math.round(FAB_RADIUS * 0.62);
    const avstand = FAB_RADIUS * 2 + 20;

    for (let i = 0; i < aksjoner.length; i += 1) {
      const a = aksjoner[i];
      // Fan opp og til venstre — vinkler 90° og 180° (top og venstre)
      const vinkler = aksjoner.length === 1 ? [Math.PI] : [Math.PI, -Math.PI / 2];
      const vinkel = vinkler[i] ?? Math.PI;
      const dx = Math.round(Math.cos(vinkel) * avstand);
      const dy = Math.round(Math.sin(vinkel) * avstand);
      const sluttX = fabX + dx;
      const sluttY = fabY + dy;

      const sub = this.add.container(fabX, fabY).setDepth(23).setAlpha(0);
      const subShadow = this.add
        .circle(4, 6, smaaRadius, 0x000000, 0.3)
        .setOrigin(0.5);
      sub.add(subShadow);
      const subBg = this.add
        .circle(0, 0, smaaRadius, COLORS.wine, 1)
        .setOrigin(0.5)
        .setStrokeStyle(3, COLORS.white, 0.85);
      sub.add(subBg);
      // Runde 39: foretrekker custom WebP-ikon (64x64).
      // Runde 45: emoji-fallback fjernet. Hvis ikon-teksturen ikke er
      // lastet (skal aldri skje), tegner vi en simpel hvit ring så
      // sub-FAB-en fortsatt er synlig som klikkbar.
      if (a.ikonAssetKey && this.textures.exists(a.ikonAssetKey)) {
        const subImg = this.add.image(0, 0, a.ikonAssetKey).setOrigin(0.5);
        const tex = this.textures.get(a.ikonAssetKey).getSourceImage();
        const w = (tex as HTMLImageElement).width || 1;
        const h = (tex as HTMLImageElement).height || 1;
        const target = 64;
        const scale = Math.min(target / w, target / h);
        subImg.setScale(scale);
        sub.add(subImg);
      } else {
        const ring = this.add
          .circle(0, 0, smaaRadius * 0.4, COLORS.white, 0)
          .setStrokeStyle(3, COLORS.white, 1);
        sub.add(ring);
      }

      if (a.badge !== undefined && a.badge > 0) {
        const badgeBg = this.add
          .circle(smaaRadius - 10, -smaaRadius + 10, 20, COLORS.yellowWarm, 1)
          .setOrigin(0.5)
          .setStrokeStyle(2, COLORS.ink, 0.4);
        sub.add(badgeBg);
        const badgeTxt = this.add
          .text(smaaRadius - 10, -smaaRadius + 10, String(a.badge), {
            fontFamily: FONTS.hud,
            fontSize: fontStr(18),
            color: '#1a1a1a',
            fontStyle: '700',
          })
          .setOrigin(0.5);
        sub.add(badgeTxt);
      }

      subBg.setInteractive({ useHandCursor: true });
      subBg.on('pointerover', () => subBg.setFillStyle(0x6f1f2c));
      subBg.on('pointerout', () => subBg.setFillStyle(COLORS.wine));
      subBg.on('pointerup', () => {
        sfx.play('click');
        haptisk.lett();
        a.onClick();
      });

      if (foretrekkerRedusertBevegelse()) {
        sub.setPosition(sluttX, sluttY);
        sub.setAlpha(1);
      } else {
        this.tweens.add({
          targets: sub,
          x: sluttX,
          y: sluttY,
          alpha: 1,
          duration: 200,
          ease: 'Back.easeOut',
        });
      }

      this.aksjonsFanContainere.push(sub);
    }
  }

  private kollapsAksjonsFan(): void {
    if (!this.aksjonsFanExpandert) return;
    this.aksjonsFanExpandert = false;
    this.aksjonsFanForEtasje = undefined;
    if (this.aksjonsBackdrop) {
      this.aksjonsBackdrop.destroy();
      this.aksjonsBackdrop = undefined;
    }
    for (const c of this.aksjonsFanContainere) {
      c.destroy(true);
    }
    this.aksjonsFanContainere = [];
  }

  private byggRomMedBilde(
    textureKey: string,
    tittel: string,
    undertittel: string,
    harVideo = false,
  ): Phaser.GameObjects.Container {
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const layer = this.add.container(0, 0).setDepth(2);

    if (harVideo) {
      void tittel;
      void undertittel;
      return layer;
    }

    const bg = this.add
      .rectangle(0, 0, roomWidth, GAME_HEIGHT, COLORS.beige, 1)
      .setOrigin(0, 0);
    layer.add(bg);

    if (this.textures.exists(textureKey)) {
      const img = this.add
        .image(roomWidth / 2, GAME_HEIGHT / 2, textureKey)
        .setOrigin(0.5);
      const tex = this.textures.get(textureKey).getSourceImage();
      const texW = (tex as HTMLImageElement).width || 1;
      const texH = (tex as HTMLImageElement).height || 1;
      const scale = Math.min(roomWidth / texW, GAME_HEIGHT / texH);
      img.setScale(scale);
      layer.add(img);
    }

    const captionH = 210;
    const captionY = GAME_HEIGHT - captionH - 24;
    const caption = this.add
      .rectangle(24, captionY, roomWidth - 48, captionH, 0x1a1a1a, 0.72)
      .setOrigin(0, 0);
    layer.add(caption);

    const tittelTekst = this.add
      .text(roomWidth / 2, 0, tittel, {
        fontFamily: FONTS.display,
        fontSize: fontStr(46),
        color: '#ffffff',
      })
      .setOrigin(0.5, 0);
    layer.add(tittelTekst);

    const undertittelTekst = this.add
      .text(roomWidth / 2, 0, undertittel, {
        fontFamily: FONTS.body,
        fontSize: fontStr(22),
        color: '#f4ead5',
        fontStyle: 'italic',
        wordWrap: { width: roomWidth - 96 },
        align: 'center',
      })
      .setOrigin(0.5, 0);
    layer.add(undertittelTekst);

    // Layout-primitiv (2026-06-04): sentrert tittel + undertittel stables fra
    // målt høyde (origin 0.5,0 → kun y settes, x forblir sentrert).
    const capTekst = new Kolonne(roomWidth / 2, captionY + 32, 16);
    capTekst.legg(tittelTekst);
    capTekst.legg(undertittelTekst);

    return layer;
  }

  private drawCaption(): void {
    // captionW må slutte før etasje-panelet (x = GAME_WIDTH - PANEL_WIDTH).
    // Margin 16 px så Playfair-tittel og beskrivelse aldri overlapper med
    // floor-rad-tekstene i panelet (særlig "Låst (Krever tier C)").
    const captionX = 56;
    const captionY = GAME_HEIGHT - 420;
    const captionW = GAME_WIDTH - PANEL_WIDTH - captionX - 16;
    const captionH = 220;

    // Drop-shadow for depth (runde 37)
    this.add
      .rectangle(captionX - 8, captionY - 6, captionW, captionH, 0x000000, 0.28)
      .setOrigin(0, 0);

    this.add
      .rectangle(captionX - 12, captionY - 12, captionW, captionH, COLORS.white, 0.92)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.ink, 0.2);

    // Subtle vinrød top-accent-bar
    this.add
      .rectangle(captionX - 12, captionY - 12, captionW, 4, COLORS.wine, 0.85)
      .setOrigin(0, 0);

    this.floorLabel = this.add
      .text(captionX, 0, 'Redaksjon', {
        fontFamily: FONTS.display,
        fontSize: fontStr(52),
        color: '#862736',
        wordWrap: { width: captionW - 36 },
      })
      .setOrigin(0, 0);

    this.floorDesc = this.add
      .text(captionX, 0, '', {
        fontFamily: FONTS.body,
        fontSize: fontStr(26),
        color: '#1a1a1a',
        wordWrap: { width: captionW - 36 },
      })
      .setOrigin(0, 0);

    // Layout-primitiv (2026-06-04): label + beskrivelse stables fra målt
    // høyde i stedet for hardkodet desc-y=captionY+86. Playfair-label måles
    // høyere enn nominell 52px, så den gamle faste avstanden ga kun ~6px
    // klaring og kunne treffe beskrivelsen.
    const tekst = new Kolonne(captionX, captionY, 16);
    tekst.legg(this.floorLabel);
    tekst.legg(this.floorDesc);

    this.refreshActiveFloor(gameState.aktivEtasje);
  }

  private refreshActiveFloor(id: FloorId): void {
    const floor = findFloor(id);
    if (!floor) return;
    this.floorLabel?.setText(floor.label);
    this.floorDesc?.setText(floor.beskrivelse);
  }

  private visOvergangs(overganger: FaseOvergang[]): void {
    if (!this.toasts) return;
    let vanligeOverganger = 0;
    for (const o of overganger) {
      if (o.tilStatus === 'utgitt') {
        // Utgivelse får sin egen seremoni (LanseringModal) i stedet for en
        // stille toast. Slå opp boka for forfatter/tier/sjanger.
        const bok =
          gameState.iProduksjon.find((b) => b.isbn === o.isbn) ??
          gameState.utgitteBoker.find((b) => b.isbn === o.isbn);
        this.lanseringKo.push({
          tittel: o.tittel,
          forfatter: bok?.author ?? '',
          tier: bok?.tier ?? 'D',
          genre: bok?.genre ?? '',
        });
      } else {
        this.toasts.vis(`«${o.tittel}» ${overgangFrase(o)}`);
        vanligeOverganger += 1;
      }
    }
    // Spark + bell for de vanlige fase-overgangene; lanseringen har sin egen
    // konfetti og cha-ching.
    if (vanligeOverganger > 0 && this.produksjonsBoksBounds) {
      const b = this.produksjonsBoksBounds;
      this.partikler?.spark(this, b.x + b.w / 2, b.y + b.h / 2);
      sfx.play('bell');
    }
    this.pumpSeremonier();
  }

  /**
   * Viser neste seremoni i køene, hvis ingen allerede står åpen. Lansering
   * (utgivelse) går foran dom-dag (anmeldelse), siden utgivelsen kommer
   * først i tid. Pauser auto-tikket mens en seremoni står.
   */
  private pumpSeremonier(): void {
    const lans = this.lanseringModal;
    const dom = this.domDagModal;
    if (!lans || !dom) return;
    if (lans.isOpen || dom.isOpen) return;

    const nesteLans = this.lanseringKo.shift();
    if (nesteLans) {
      autoSpeed.paus();
      lans.open(nesteLans, () => this.etterSeremoni());
      return;
    }
    const nesteDom = this.domDagKo.shift();
    if (nesteDom) {
      autoSpeed.paus();
      dom.open(nesteDom, () => this.etterSeremoni());
    }
  }

  private etterSeremoni(): void {
    if (this.lanseringKo.length > 0 || this.domDagKo.length > 0) {
      this.pumpSeremonier();
    } else {
      autoSpeed.fortsett();
    }
  }

  private visAnmeldelser(hendelser: AnmeldelseHendelse[]): void {
    if (!this.toasts || hendelser.length === 0) return;

    // Grupper anmeldelsene per bok. Nye bøker får alle fire kritikerne
    // samme dag (planleggAnmeldelser) → vises som én iscenesatt dom-dag.
    // Enkeltstående anmeldelser (f.eks. eldre lagringer med spredte dager)
    // faller tilbake til den gamle toast-visningen.
    const perBok = new Map<string, AnmeldelseHendelse[]>();
    for (const h of hendelser) {
      const liste = perBok.get(h.isbn);
      if (liste) liste.push(h);
      else perBok.set(h.isbn, [h]);
    }

    for (const gruppe of perBok.values()) {
      if (gruppe.length >= 2) {
        this.domDagKo.push({
          tittel: gruppe[0].tittel,
          forfatterNavn: gruppe[0].forfatterNavn,
          dommer: gruppe.map((h) => ({
            kritikerNavn: h.kritikerNavn,
            terninger: h.anmeldelse.terninger,
            sitat: h.anmeldelse.sitat,
          })),
        });
      } else {
        const h = gruppe[0];
        sfx.play('pageturn');
        const stjerner = stjerneStreng(h.anmeldelse.terninger);
        const utdrag = forkortSitat(h.anmeldelse.sitat);
        this.toasts.vis(
          `${h.kritikerNavn} ga «${h.tittel}» ${stjerner}\n«${utdrag}»`,
          'anmeldelse',
        );
      }
    }

    this.pumpSeremonier();
  }

  private visSalgsMilestoner(hendelser: SalgHendelse[]): void {
    if (!this.toasts) return;
    if (hendelser.length > 0) {
      sfx.play('cha-ching');
      this.partikler?.cha(this, GAME_WIDTH / 2, GAME_HEIGHT - 240);
    }
    for (const h of hendelser) {
      this.toasts.vis(`«${h.tittel}» ${h.melding}`, 'milestone');
    }
  }

  private visKampanjeStartet(h: KampanjeStartetHendelse): void {
    if (!this.toasts) return;
    this.toasts.vis(`Kampanje «${h.kanalNavn}» startet for «${h.bokTittel}»`, 'standard');
  }

  private visKampanjeFerdig(hendelser: KampanjeFerdigHendelse[]): void {
    if (!this.toasts) return;
    for (const h of hendelser) {
      const melding = `Kampanje «${h.kanalNavn}» for «${h.bokTittel}» er ferdig — solgte ${h.salgIPerioden.toLocaleString('nb-NO')} eksemplarer i perioden`;
      const variant = h.salgIPerioden >= KAMPANJE_SALG_MILESTONE ? 'milestone' : 'kurs';
      this.toasts.vis(melding, variant);
    }
  }

  private visForfatterManus(hendelser: ForfatterManusHendelse[]): void {
    if (!this.toasts) return;
    for (const h of hendelser) {
      this.toasts.vis(
        `Nytt manus fra ${h.forfatterNavn}: «${h.tittel}» (${h.tier})`,
        'milestone',
      );
    }
  }

  private visSesongSkift(sesong: Sesong): void {
    if (!this.toasts) return;
    // Filmatisk DOM-overlay (800ms total — toner inn/ut over canvas)
    sesongOvergang.vis(sesong);
    this.toasts.vis(
      `${SESONG_NAVN[sesong]} er her\n${SESONG_BESKRIVELSE[sesong]}`,
      'milestone',
    );
    // Whoosh fra venstre til høyre over rom-arealet
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    this.partikler?.whoosh(this, 0, roomWidth, GAME_HEIGHT / 2);
  }

  private visKaffepratEtterMorgen(dag: number): void {
    if (!this.toasts) return;
    const sesong = sesongForDag(dag);
    const linje = tilfeldigKaffeprat(dag, sesong);
    if (!linje) return;
    this.time.delayedCall(2200, () => {
      this.toasts?.vis(linje, 'standard');
    });
  }

  private visEtasjeOppgradert(h: EtasjeOppgradertHendelse): void {
    if (!this.toasts) return;
    this.toasts.vis(
      `${h.floorLabel} er oppgradert — ${h.upgrade.navn}`,
      'milestone',
    );
  }

  private visLojalitetVarsler(hendelser: LojalitetVarselHendelse[]): void {
    if (!this.toasts) return;
    for (const h of hendelser) {
      if (h.nivaa === 'kritisk') {
        this.toasts.vis(
          `${h.forfatterNavn} er kritisk misfornøyd — risikerer å hoppe av.`,
          'standard',
        );
      } else {
        this.toasts.vis(
          `${h.forfatterNavn} virker misfornøyd. Vurder å tilby noe.`,
          'kurs',
        );
      }
    }
  }

  /**
   * Ukentlig likviditets-varsel. Drift vises diskret; står du på
   * kassakreditt skrur Kristine til, og ved bunnen advares det om at nye
   * forskudd er sperret.
   */
  private visLikviditet(h: LikviditetHendelse): void {
    if (!this.toasts) return;
    if (h.paaBunn) {
      this.toasts.vis(
        `Kassakreditten er på bunn. Kristine: «Styret krever en plan, Sven — ` +
          `ingen nye forskudd før tallene er over streken.»`,
        'standard',
      );
      return;
    }
    if (h.paaKreditt) {
      this.toasts.vis(
        `Ukesdrift −${h.drift.toLocaleString('nb-NO')} kr · rente −${h.rente.toLocaleString('nb-NO')} kr. ` +
          `Kristine: «Du står på kassakreditt nå. Få tall i pluss.»`,
        'standard',
      );
      return;
    }
    this.toasts.vis(`Ukesdrift: −${h.drift.toLocaleString('nb-NO')} kr`, 'standard');
  }

  private refreshFloorUpgradeOverlay(id: FloorId): void {
    this.floorUpgradeOverlay?.destroy(true);
    this.floorUpgradeOverlay = undefined;

    const floor = findFloor(id);
    if (!floor) return;
    const status = this.effektivStatusFor(floor);
    if (status !== 'aapen') return;
    if (id === 'marked' || id === 'direksjon') return;

    // GAME_WIDTH - PANEL_WIDTH = 720 er venstre-kant til etasje-panelet.
    // Overlay må slutte før det med en sikker margin på 16 px så
    // "Oppgrader"-knappen aldri krasjer med floor-rad i panelet.
    const captionX = 56;
    const captionY = GAME_HEIGHT - 420;
    const overlayW = GAME_WIDTH - PANEL_WIDTH - captionX - 16;
    const overlayH = 92;
    const overlayX = captionX;
    const overlayY = captionY - overlayH - 24;

    const layer = this.add.container(0, 0).setDepth(3);

    if (gameState.erOppgradert(id)) {
      const badge = this.add
        .rectangle(overlayX, overlayY, 250, 56, COLORS.forest, 1)
        .setOrigin(0, 0);
      const tekst = this.add
        .text(overlayX + 125, overlayY + 28, '✓ Oppgradert', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      layer.add([badge, tekst]);
    } else {
      const upgrade = finnFloorUpgrade(id);
      if (!upgrade) return;
      const bg = this.add
        .rectangle(overlayX, overlayY, overlayW, overlayH, COLORS.ochre, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.oakDark, 0.6);
      layer.add(bg);

      const knappW = 220;
      const knappH = 64;
      const knappX = overlayX + overlayW - knappW - 14;
      const knappY = overlayY + (overlayH - knappH) / 2;

      const tekst = this.add
        .text(
          overlayX + 18,
          overlayY + overlayH / 2,
          `Oppgrader for ${upgrade.pris.toLocaleString('nb-NO')} kr`,
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(22),
            color: '#1a1a1a',
            wordWrap: { width: overlayW - knappW - 50 },
          },
        )
        .setOrigin(0, 0.5);
      layer.add(tekst);
      const knappBg = this.add
        .rectangle(knappX, knappY, knappW, knappH, COLORS.wine, 1)
        .setOrigin(0, 0);
      const knappTxt = this.add
        .text(knappX + knappW / 2, knappY + knappH / 2, 'Oppgrader  →', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(22),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      layer.add([knappBg, knappTxt]);

      knappBg.setInteractive({ useHandCursor: true });
      knappBg.on('pointerover', () => knappBg.setFillStyle(0x6f1f2c));
      knappBg.on('pointerout', () => knappBg.setFillStyle(COLORS.wine));
      knappBg.on('pointerup', () => this.aapneFloorUpgrade(id));

      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => bg.setFillStyle(COLORS.yellowWarm, 1));
      bg.on('pointerout', () => bg.setFillStyle(COLORS.ochre, 1));
      bg.on('pointerup', () => this.aapneFloorUpgrade(id));
    }

    this.floorUpgradeOverlay = layer;
  }

  private aapneFloorUpgrade(id: FloorId): void {
    this.floorUpgradeModal?.open(id);
  }

  private visKursFerdig(hendelser: KursHendelse[]): void {
    if (!this.toasts) return;
    for (const h of hendelser) {
      const boostStr = Object.entries(h.boosts)
        .map(([k, v]) => `+${v} ${kort(k)}`)
        .join(' · ');
      this.toasts.vis(
        `${h.forfatterNavn} er tilbake fra «${h.kursTittel}»\n${boostStr}`,
        'standard',
      );
      this.toasts.vis(
        `${h.forfatterNavn} sier kurset var livsforandrende.\nSe kurset hos Forfatterskolen ↗`,
        {
          variant: 'kurs',
          onClick: () => {
            if (typeof window !== 'undefined' && h.kursUrl) {
              window.open(h.kursUrl, '_blank', 'noopener');
            }
          },
        },
      );
    }
  }
}

function kort(stat: string): string {
  const labels: Record<string, string> = {
    dialog: 'Dialog',
    plot: 'Plot',
    karakter: 'Karakter',
    spraak: 'Språk',
    voice: 'Voice',
    autentisitet: 'Autentisitet',
    disiplin: 'Disiplin',
  };
  return labels[stat] ?? stat;
}

function forkortSitat(sitat: string, maks = 80): string {
  if (sitat.length <= maks) return sitat;
  const cutoff = sitat.lastIndexOf(' ', maks);
  const slutt = cutoff > 30 ? cutoff : maks;
  return `${sitat.slice(0, slutt).trim()}…`;
}

function overgangFrase(o: FaseOvergang): string {
  switch (o.tilStatus) {
    case 'redigert':
      return 'er ferdig redigert — klar for design';
    case 'designet':
      return 'er ferdig designet — klar for trykk';
    case 'utgitt':
      return 'er trykket — venter på anmeldelser';
    default:
      return 'har gått videre i pipelinen';
  }
}

function countDineNominasjoner(
  kart: Record<BrageKategori, BrageNominasjon[]>,
): number {
  let n = 0;
  for (const liste of Object.values(kart)) {
    for (const nom of liste) {
      if (nom.forlag === 'spiller') n += 1;
    }
  }
  return n;
}

/**
 * Mapper FAB-asset-key til lesbart navn for skjermlesere. Brukes av
 * AriaMirror når fan-menyen ekspanderer.
 *
 * Runde 45: emoji-fallbacker fjernet; alle FAB-aksjoner garanteres å ha
 * dedikert `ikonAssetKey`.
 */
function fabLabelFor(assetKey?: string): string {
  switch (assetKey) {
    case ASSET_KEYS.iconFabInbox:
      return 'Innboks';
    case ASSET_KEYS.iconFabForfattere:
      return 'Forfattere';
    case ASSET_KEYS.iconFabBokhandel:
      return 'Bokhandel';
    case ASSET_KEYS.iconFabKaffekopp:
      return 'Kaffeprat';
    case ASSET_KEYS.iconFabPalett:
      return 'Design';
    case ASSET_KEYS.iconFabUtenlands:
      return 'Internasjonalt salg';
    case ASSET_KEYS.iconFabMegafon:
      return 'Kampanjer';
    case ASSET_KEYS.iconFabSkrivelab:
      return 'Skrivelab';
    case ASSET_KEYS.iconFabAksjon:
      return 'Aksjoner';
  }
  return 'Aksjon';
}
